import React from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import ProceedQuickReplies from '../../components/ui/ProceedQuickReplies';
import PropDropdown from './PropDropdown';
import { asksToProceed, parseQuickPrompt, proceedPromptFromEvent, type AiQuickPrompt } from '../../utils/proceedPrompt';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { FiRefreshCw, FiChevronLeft, FiChevronDown, FiBarChart2, FiPaperclip, FiSend, FiX, FiFolder, FiThumbsUp, FiThumbsDown, FiEdit3, FiMail, FiCheck, FiUser } from 'react-icons/fi';
import { agentAvatarFor, useAgentPhotosVersion } from '../../utils/agentPhotos';
import { seedInsuredKind, useInsuredKinds, fetchInsuredRecord } from '../../utils/insuredKind';
import { colorForAgent, initialsForAgent, displayNameForAgent } from '../../utils/agentAvatar';
import { RiSparklingFill } from 'react-icons/ri';
import type { CommandIssue, CommandStatus, CommentModel } from '../../context/CommandIssueContext';
import { callMomentumKanbanAI, callMomentumKanbanStream, consumeKanbanSse, createKanbanTransactionId, uploadMomentumKanbanFile, uploadMomentumKanbanAmsFiles, getKanbanFileUrl, listKanbanCustomCategories, KanbanCustomCategory, sendKanbanEmail, fetchKanbanEmailHistory, getActiveKanbanRun, openKanbanReattachStream, fetchAgencyAgents, fetchTaskSharedAgentIds, fetchKanbanTaskJson, kanbanRunError, BUDGET_REACHED_CODE, type KanbanRunError } from '../../services/kanbanAIService';
import { listDwAgents } from '../claudeagents/claudeAgentApi';
import { getStaticExecutions, toRunDetailStatus } from './viewDetailsStatic';
import SelectFromAmsDialog, { AmsUploadSelection } from './SelectFromAmsDialog';
import ThinkingBlock from '../chat/ThinkingBlock';
import FeedbackEditorPopover from '../../components/ui/FeedbackEditorPopover';
import { useTheme } from '../../context/ThemeContext';
import { useInsuredMention } from '../chat/useInsuredMention';
import InsuredMentionDropdown from '../chat/InsuredMentionDropdown';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { parseKanbanDate, kanbanTsToIso } from '../../utils/dateTime';
import { usePolicyDetail } from '../../context/PolicyDetailContext';
import { useRunDetailPanel } from '../../context/RunDetailPanelContext';
import { useActiveAgent, useAgentReplySignal } from '../../context/ActiveAgentContext';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import aiLogo from '../../assets/images/M_Multicolor.svg';
import '../../styles/InsuredDetail.css';
import '../../styles/ChatMessage.css';

const FONT = "var(--font-sans, 'Poppins', sans-serif)";

const MD_HEADING_COMPONENTS = {
  h1: ({ children }: any) => <h1 style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, margin: '8px 0 5px', lineHeight: 1.35, color: 'var(--text-primary)' }}>{children}</h1>,
  h2: ({ children }: any) => <h2 style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, margin: '7px 0 4px', lineHeight: 1.35, color: 'var(--text-primary)' }}>{children}</h2>,
  h3: ({ children }: any) => <h3 style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, margin: '6px 0 4px', lineHeight: 1.4, color: 'var(--text-primary)' }}>{children}</h3>,
  h4: ({ children }: any) => <h4 style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, margin: '6px 0 3px', lineHeight: 1.4, color: 'var(--text-primary)' }}>{children}</h4>,
  h5: ({ children }: any) => <h5 style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, margin: '5px 0 3px', lineHeight: 1.45, color: 'var(--text-secondary)' }}>{children}</h5>,
  h6: ({ children }: any) => <h6 style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, margin: '4px 0 2px', lineHeight: 1.45, color: 'var(--text-secondary)', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{children}</h6>,
};

// Some backends stream the answer as small deltas; others re-send the full
// text-so-far on every event — including, sometimes, a freshly regenerated
// full answer that isn't a literal continuation of what's accumulated (e.g. a
// DW agent handoff, or a draft pass followed by a differently-worded final
// one). Blindly appending in that case glues two complete answers together
// with no separator. Layered guards, most to least specific:
//   1) growing full-text-so-far (`next` extends `prev`) — take it as-is.
//   2) nothing accumulated yet — take it as-is.
//   3) a large resend whose opening clearly diverges from what we have — a
//      fresh answer, not a delta.
//   4) safety net: once we've already accumulated a substantial answer
//      (guard #3's 200-char threshold can still be dodged by a shorter
//      resend, or one that happens to open with the same heading as what
//      we have), a further chunk at least as long as everything so far
//      can't be a genuine token delta — still a fresh answer, not a delta.
//      Gated on `prev` already being sizeable so it doesn't misfire early
//      in a stream, when the first delta chunks are naturally longer than
//      the still-tiny accumulated buffer.
// Only a chunk that's genuinely short relative to what we have — a real delta
// — falls through to the append at the end.
const foldAnswerChunk = (prev: string, next: string): string => {
  if (!prev || next.startsWith(prev)) return next;
  if (next.length > 200 && !prev.startsWith(next.slice(0, 50))) return next;
  if (prev.length > 100 && next.length >= prev.length) return next;
  return prev + next;
};

// The momentum-kanban SSE reader lives in kanbanAIService.ts (consumeKanbanSse)
// now that the backend streams the answer as live token fragments: the shared
// reader adds the same smoothed answer reveal claudeAgentApi.ts's pumpSse gives
// /claude-managed/chat, and both the POST stream and the re-attach stream here
// render an identical event contract through identical logic.

const toCommentsJson = (comments: CommentModel[]): string =>
  JSON.stringify(comments.map(c => ({
    ...(c.id ? { Id: c.id } : {}),
    Role: c.role,
    Message: c.message,
    ...(c.created ? { Created: c.created } : {}),
    IsLiked: c.isLiked ?? false,
    IsDisliked: c.isDisliked ?? false,
    Feedback: c.feedback ?? '',
    UserId: c.userId ?? '',
    FirstName: c.firstName ?? '',
    LastName: c.lastName ?? '',
  })));

// Identity of the logged-in operator, stamped onto every comment this panel
// creates — the same three fields the backend stamps from the JWT igtoken.
const operatorIdentity = (): Pick<CommentModel, 'userId' | 'firstName' | 'lastName'> => ({
  userId: localStorage.getItem('userId') ?? '',
  firstName: localStorage.getItem('firstName') ?? '',
  lastName: localStorage.getItem('lastName') ?? '',
});

const fmtCommentDate = (value?: string): string => {
  const d = parseKanbanDate(value);
  if (!d) return '';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return 'Attached';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const COMMAND_COLUMNS: { key: CommandStatus; label: string; color: string }[] = [
  { key: 'todo',       label: 'To do',      color: '#6366f1' },
  { key: 'inprogress', label: 'In progress', color: '#f59e0b' },
  { key: 'inreview',   label: 'In review',  color: '#8b5cf6' },
  { key: 'done',       label: 'Done',       color: '#22c55e' },
];

const ASSIGNEE_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'User' },
];

const SOURCE_LABELS: Record<number, string> = {
  1: 'COI Request',
  2: 'Notice Of Cancellation',
  3: 'Policy Change',
  4: 'Payment Reminder',
  5: 'Renewal Notification',
  6: 'Service Request',
  7: 'Task',
  8: 'Lead Quote Request',
};

const CATEGORY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'COI Request' },
  { value: 2, label: 'Notice Of Cancellation' },
  { value: 3, label: 'Policy Change' },
  { value: 4, label: 'Payment Reminder' },
  { value: 5, label: 'Renewal Notification' },
  { value: 6, label: 'Service Request' },
  { value: 7, label: 'Task' },
  { value: 8, label: 'Lead Quote Request' },
];
const MOMENTUM_IDS_RE = /<!--MOMENTUM_IDS:(\{[^}]*\})-->/;

const KANBAN_STATUS_IDS: Record<CommandStatus, string> = {
  todo:       '11111111-1111-1111-1111-111111111111',
  inprogress: '22222222-2222-2222-2222-222222222222',
  inreview:   '33333333-3333-3333-3333-333333333333',
  done:       '44444444-4444-4444-4444-444444444444',
};

interface ParsedEmailDraft {
  to: string;
  subject: string;
  body: string;
  cc: string;
}

// Strip $$PDF_Name##guid$$ and $$IDF_Name##guid$$ tokens → just "Name"
const stripEntityTokens = (text: string): string =>
  text.replace(/\$\$(?:PDF|IDF)_([^#]+)##[^$]+\$\$/g, '$1');

const parseEmailDrafts = (message: string): ParsedEmailDraft[] => {
  const drafts: ParsedEmailDraft[] = [];

  // Pattern 1: Bold-label format — **To:** value, **Subject:** value, **Body:** ...rest
  const draftBlockRe = /(?:^|\n)>?\s*\*\*(?:To|Recipient)\s*:\*\*\s*(.+?)(?:\n)(?:[\s>]*\*\*(?:CC|Cc)\s*:\*\*\s*(.+?)\n)?[\s>]*\*\*Subject\s*:\*\*\s*(.+?)(?:\n)([\s\S]*?)(?=\n---|\n##|\n\*\*To\s*:\*\*|\n>\s*\*\*To\s*:\*\*|$)/gi;

  let match: RegExpExecArray | null;
  while ((match = draftBlockRe.exec(message)) !== null) {
    const to = stripEntityTokens((match[1] || '').trim());
    const cc = stripEntityTokens((match[2] || '').trim());
    const subject = stripEntityTokens((match[3] || '').trim());
    let body = (match[4] || '').trim();
    body = body.replace(/^>?\s*\*\*Body\s*:\*\*\s*/i, '').trim();
    body = body.replace(/^>\s?/gm, '').trim();
    body = body.replace(/^```(?:html)?\s*\n?([\s\S]*?)\n?```\s*$/i, '$1').trim();
    body = stripEntityTokens(body);
    if (to && subject) {
      drafts.push({ to, subject, body, cc });
    }
  }

  // Pattern 2: Subject-first format — **Subject:** before **To:**
  if (drafts.length === 0) {
    const altRe = /(?:^|\n)>?\s*\*\*Subject\s*:\*\*\s*(.+?)(?:\n)[\s>]*\*\*(?:To|Recipient)\s*:\*\*\s*(.+?)(?:\n)(?:[\s>]*\*\*(?:CC|Cc)\s*:\*\*\s*(.+?)\n)?([\s\S]*?)(?=\n---|\n##|\n\*\*Subject\s*:\*\*|\n>\s*\*\*Subject\s*:\*\*|$)/gi;
    while ((match = altRe.exec(message)) !== null) {
      const subject = stripEntityTokens((match[1] || '').trim());
      const to = stripEntityTokens((match[2] || '').trim());
      const cc = stripEntityTokens((match[3] || '').trim());
      let body = (match[4] || '').trim();
      body = body.replace(/^>?\s*\*\*Body\s*:\*\*\s*/i, '').trim();
      body = body.replace(/^>\s?/gm, '').trim();
      body = body.replace(/^```(?:html)?\s*\n?([\s\S]*?)\n?```\s*$/i, '$1').trim();
      body = stripEntityTokens(body);
      if (to && subject) {
        drafts.push({ to, subject, body, cc });
      }
    }
  }

  return drafts;
};

// A shared task's Human comments can come from any teammate it's been shared
// with, not just the viewer — the API now tags each one with userId/first/
// lastName so we can tell them apart instead of labeling every Human comment
// "You". AI comments still show the task's assigned agent name when there is
// one (e.g. "Account Manager Agent"), falling back to generic "AI".
const getCommentAuthorLabel = (c: CommentModel, assigneeAgentName?: string): string => {
  if ((c.role || '').toLowerCase() === 'human') {
    const selfUserId = (localStorage.getItem('userId') || '').trim();
    const commentUserId = c.userId != null ? String(c.userId).trim() : '';
    if (!commentUserId || commentUserId === selfUserId) return 'You';
    return `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Teammate';
  }
  return (assigneeAgentName || '').trim() || 'AI';
};

// Mirrors ChatMessage.tsx's getUserInitials so the viewer's own comment shows
// their real initials instead of a placeholder. Reads localStorage fresh on
// every call (not cached at module load) so a logout/login as a different
// user without a page reload shows the right initials.
function getSelfInitials(): string {
  const f = localStorage.getItem('firstName') ?? '';
  const l = localStorage.getItem('lastName') ?? '';
  const first = f.charAt(0).toUpperCase();
  const last = l.charAt(0).toUpperCase();
  return first + last || first || (localStorage.getItem('userName') ?? '?').charAt(0).toUpperCase();
}

const getCommentInitials = (c: CommentModel, authorLabel: string): string => {
  if ((c.role || '').toLowerCase() !== 'human') return 'AI';
  if (authorLabel === 'You') return getSelfInitials();
  const first = (c.firstName || '').trim().charAt(0);
  const last = (c.lastName || '').trim().charAt(0);
  return (first + last).toUpperCase() || authorLabel.slice(0, 2).toUpperCase();
};

// Convert $$IDF_Name##id$$ / $$PDF_Name##id$$ tokens to markdown links so
// ReactMarkdown can parse table structure intact, then we intercept the <a> render.
const processEntityLinks = (text: string): string =>
  text
    .replace(/\$\$IDF_([^#$]+)##([^$]+)\$\$/g, (_, name, id) => `[${name.trim()}](#INSURED_ID_${id.trim()})`)
    .replace(/\$\$PDF_([^#$]+)##([^$]+)\$\$/g, (_, name, id) => `[${name.trim()}](#POLICY_ID_${id.trim()})`);

const parseCommentDisplay = (raw: string): { body: string; attachments: string[] } => {
  const lines = raw.split('\n');
  const bodyLines: string[] = [];
  const attachments: string[] = [];
  let inAttachmentBlock = false;
  for (const line of lines) {
    if (/^\s*Attached files:\s*$/i.test(line)) {
      inAttachmentBlock = true;
      continue;
    }

    if (!inAttachmentBlock) {
      bodyLines.push(line);
      continue;
    }

    if (/^\s*$/.test(line)) continue;
    if (!/^\s*-\s+/.test(line)) {
      inAttachmentBlock = false;
      bodyLines.push(line);
      continue;
    }

    const bracketName = line.match(/^\s*-\s*\[([^\]]+)\]/)?.[1]?.trim();
    if (bracketName) {
      const nameNoSize = bracketName.replace(/\s*\(\d+(?:\.\d+)?\s*(?:B|KB|MB)\)\s*$/i, '').trim();
      attachments.push(nameNoSize || 'Attached file');
      continue;
    }

    const cleaned = line
      .replace(/^\s*-\s*/, '')
      .replace(/\[([^\]]+)\]\((?:\[[^\]]+\]\([^)]+\)|[^)])+\)/g, '$1')
      .replace(/https?:\/\/\S+/gi, '')
      .replace(/\s*\(\d+(?:\.\d+)?\s*(?:B|KB|MB)\)\s*$/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

    attachments.push(cleaned || 'Attached file');
  }

  return {
    body: bodyLines.join('\n'),
    attachments,
  };
};

const normalizeAgentName = (value: unknown): string => {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return '';
  const lowered = name.toLowerCase();
  // 'User' is a placeholder this panel used to write into agent_name whenever
  // the assignee was a human, which destroyed the real agent attribution.
  // Legacy rows still carry it — treat it as "no agent" so it is neither
  // rendered nor written back. Keep in sync with MomentumCommandPage.
  if (lowered === 'null' || lowered === 'undefined' || lowered === 'user') return '';
  return name;
};

// One-time auto-run keys that survive component remounts (e.g. React StrictMode
// mount/unmount/mount in development), preventing duplicate first-run AI notes.
const processedAutoRunKeys = new Set<string>();


// Replaces @Name mentions with $$IDF_Name##id$$ / $$PDF_Name##id$$ annotations
// (policy = PDF, everything else = IDF) — the format comments are saved in.
// Used for messages typed in the global chat bar while it is this panel's composer.
function annotateWith(text: string, entities: Map<string, { id: string; type: number }>): string {
  if (!entities.size) return text;
  let result = text;
  entities.forEach((entity, name) => {
    const prefix = entity.type === 700 ? 'PDF' : 'IDF';
    result = result.split(name).join(`$$${prefix}_${name}##${entity.id}$$`);
  });
  return result;
}

// ── Email draft send guard ─────────────────────────────────────────────────
// Recipients may be comma/semicolon separated. Deliberately loose: it only
// catches empty lists and obvious typos before an email reaches an insured.
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const splitRecipients = (value: string): string[] =>
  value.split(/[,;]/).map(s => s.trim()).filter(Boolean);
const validateDraftForSend = (draft: ParsedEmailDraft): string => {
  const to = splitRecipients(draft.to || '');
  if (!to.length || to.some(addr => !EMAIL_RE.test(addr))) return 'Add a valid recipient email address.';
  const cc = splitRecipients(draft.cc || '');
  if (cc.some(addr => !EMAIL_RE.test(addr))) return 'Check the CC field: each entry must be a valid email address.';
  if (!(draft.subject || '').trim()) return 'Add a subject before sending.';
  return '';
};
// First couple of non-empty lines of an HTML email body, as plain text.
const draftBodyPreview = (html: string, maxLines = 2): string[] => {
  if (!html) return [];
  const withBreaks = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n');
  let text = withBreaks.replace(/<[^>]*>/g, '');
  try {
    text = new DOMParser().parseFromString(text, 'text/html').documentElement.textContent || text;
  } catch { /* keep the tag-stripped text */ }
  return text.split('\n').map(l => l.replace(/\s+/g, ' ').trim()).filter(Boolean).slice(0, maxLines)
    .map(l => (l.length > 140 ? `${l.slice(0, 140)}…` : l));
};

// Confirmation dialog — same vocabulary as Command Center's delete dialogs
// (mc-dialog, role="alertdialog", focus starts on the safe choice, Escape and
// scrim click cancel). Portaled so the panel's overflow can't clip it; focus
// returns to whatever opened it.
const TaskConfirmDialog: React.FC<{
  idBase: string;
  title: string;
  description: React.ReactNode;
  summary?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ idBase, title, description, summary, confirmLabel, cancelLabel, tone = 'primary', onConfirm, onCancel }) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    return () => {
      if (opener && document.contains(opener)) setTimeout(() => opener.focus(), 0);
    };
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return ReactDOM.createPortal(
    <div className="mc-dialog-scrim mc-tp-dialog-scrim" onClick={onCancel}>
      <div
        ref={dialogRef}
        className="mc-dialog mc-tp-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${idBase}-title`}
        aria-describedby={`${idBase}-desc`}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <h2 id={`${idBase}-title`} className="mc-dialog__title">{title}</h2>
        {summary && <div className="mc-dialog__summary">{summary}</div>}
        <p id={`${idBase}-desc`} className="mc-dialog__text">{description}</p>
        <div className="mc-dialog__actions">
          <button type="button" className="mc-dialog__btn" autoFocus onClick={onCancel}>{cancelLabel}</button>
          <button
            type="button"
            className={`mc-dialog__btn ${tone === 'danger' ? 'mc-dialog__btn--danger' : 'mc-tp-dialog__btn--primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CommandIssuePanel: React.FC<{
  issue: CommandIssue;
  mode: 'create' | 'edit';
  autoRunPrompt?: string;
  // Bumped (to a new value) by the board header's "Proceed" button to move an
  // agent-assigned To Do task into In Progress and kick off its run. A plain
  // ref comparison (not a content-keyed module Set like processedAutoRunKeys)
  // so clicking Proceed again after a failed attempt isn't silently ignored.
  proceedSignal?: number;
  position?: number;
  source?: number;
  categoryId?: number;
  isPolling?: boolean;
  externalBusy?: boolean;
  linkedLimit?: number;
  splitTitleRow?: boolean;
  loadingFullDetail?: boolean;
  previewFileRequest?: { key: string; name: string; url: string } | null;
  onPreviewFileRequestConsumed?: () => void;
  // Reports the files this panel holds (saved + uploaded this session) so the
  // page's details header, which owns the FILES chips in onBack mode, can show
  // uploads before the task list refetches.
  onFilesChange?: (issueId: string, files: Array<{ name: string; url?: string }>) => void;
  // Momentum Command's own task list (not the Digital Workforce "Assign Task"
  // modal, which also renders this panel) — gates the "shared with" assignee
  // options below so they only ever show in Command Center.
  commandCenterMode?: boolean;
  onSave: (updated: CommandIssue) => void;
  onSaveSilent?: (updated: CommandIssue) => void;
  onClose: () => void;
  onBack?: () => void;
}> = ({ issue, mode, autoRunPrompt, proceedSignal, position = 0, source = 0, categoryId, isPolling = false, externalBusy = false, linkedLimit = 5, splitTitleRow = false, loadingFullDetail = false, previewFileRequest = null, onPreviewFileRequestConsumed, onFilesChange, commandCenterMode = false, onSave, onSaveSilent, onClose, onBack }) => {
  const [title, setTitle] = React.useState(issue.title);
  const [description, setDescription] = React.useState(issue.description || '');
  const [status, setStatus] = React.useState<CommandStatus>(issue.status);
  const [assigneeType, setAssigneeType] = React.useState<number>(issue.assigneeType ?? 0);
  const [assigneeAgentName, setAssigneeAgentName] = React.useState<string>(issue.agentName || '');
  const [assigneeAgentId, setAssigneeAgentId] = React.useState<string>(issue.agentId || '');
  const [dwAgents, setDwAgents] = React.useState<{ id: string; name: string; isPrimary: boolean; isSystem?: boolean }[]>([]);
  // The agent "Send & Assign to AI" now targets, since the standalone generic
  // "Assign to AI" dropdown choice is gone — same primary-first resolution used
  // by the create-mode default-assignee effect below.
  const primaryDwAgent = React.useMemo(() => dwAgents.find(a => a.isPrimary) || dwAgents[0], [dwAgents]);
  // Agency (human) agents this specific command has been shared with — Command
  // Center only (commandCenterMode), so they can be picked as the Assignee
  // without offering the same picker in unrelated surfaces like the Digital
  // Workforce "Assign Task" modal that also renders this panel.
  const [sharedAgents, setSharedAgents] = React.useState<{ id: string; name: string }[]>([]);
  // The shared-with teammates (agentlist + the task's sharedAgentIds) only feed the
  // Assignee control. Load them up front only when the task is assigned to a
  // teammate (needed to show who); otherwise on first open/focus of Assignee.
  const [sharedAgentsWanted, setSharedAgentsWanted] = React.useState(false);
  const wantSharedAgents = React.useCallback(() => setSharedAgentsWanted(true), []);
  React.useEffect(() => { setSharedAgentsWanted(false); }, [issue.id]);
  const assignedToTeammate = (issue.assigneeType ?? 0) === 0 && !!(issue.agentName || '').trim();
  React.useEffect(() => {
    if (!commandCenterMode || !issue.id || issue.id === '__new__') { setSharedAgents([]); return; }
    if (!sharedAgentsWanted && !assignedToTeammate) return;
    let cancelled = false;
    const selfUserId = (localStorage.getItem('userId') || '').trim();
    const selfEmail = (localStorage.getItem('userEmail') || '').trim().toLowerCase();
    Promise.all([fetchAgencyAgents(), fetchTaskSharedAgentIds(issue.id)])
      .then(([agents, sharedIds]) => {
        if (cancelled) return;
        const idSet = new Set(sharedIds);
        setSharedAgents(
          agents
            .filter(a => idSet.has(a.id))
            // Sharing already includes the current user by default on the
            // backend — they're always "User"/Me in the dropdown already, so
            // listing them again as a separate named option is redundant.
            .filter(a => {
              const isSelfById = !!selfUserId && (a.userId || '').trim() === selfUserId;
              const isSelfByEmail = !!selfEmail && (a.email || '').trim().toLowerCase() === selfEmail;
              return !isSelfById && !isSelfByEmail;
            })
            .map(a => ({ id: a.id, name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || 'Agent' })),
        );
      })
      .catch(() => { if (!cancelled) setSharedAgents([]); });
    return () => { cancelled = true; };
  }, [commandCenterMode, issue.id, sharedAgentsWanted, assignedToTeammate]);
  // Tracks whether the user has explicitly touched the Assignee selector, so the
  // create-mode default-to-primary-agent effect below doesn't fight a manual choice.
  const userChangedAssigneeRef = React.useRef(false);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number>(issue.categoryId ?? categoryId ?? 7);
  // Custom (user-created) categories are appended to the built-in TYPE options;
  // selecting one sends its row id (e.g. 1000+) as categoryId on task upsert.
  const [customCategories, setCustomCategories] = React.useState<KanbanCustomCategory[]>([]);
  const [customCategoriesLoaded, setCustomCategoriesLoaded] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    listKanbanCustomCategories().then(cats => {
      if (cancelled) return;
      setCustomCategories(cats);
      setCustomCategoriesLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);
  const categoryOptions = React.useMemo(() => {
    const options = [...CATEGORY_OPTIONS, ...customCategories.map(c => ({ value: c.id, label: c.name }))];
    // A soft-deleted custom category drops out of the list, but tasks already
    // filed under it keep its id. Keep that id selectable so the controlled
    // select doesn't silently show (and then save) a different category.
    if (customCategoriesLoaded && !options.some(o => o.value === selectedCategoryId)) {
      options.push({ value: selectedCategoryId, label: 'Deleted category' });
    }
    return options;
  }, [customCategories, customCategoriesLoaded, selectedCategoryId]);
  const [comments, setComments] = React.useState<CommentModel[]>(issue.comments ?? []);
  const [feedbackEditorIndex, setFeedbackEditorIndex] = React.useState<number | null>(null);
  const [feedbackEditorPos, setFeedbackEditorPos] = React.useState<{ top: number; left: number } | null>(null);
  const [feedbackDraft, setFeedbackDraft] = React.useState('');
  const [newComment, setNewComment] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [apiBusy, setApiBusy] = React.useState(false);
  const [statusOpen, setStatusOpen] = React.useState(false);
  const [sendDropdownOpen, setSendDropdownOpen] = React.useState(false);
  const sendDropdownRef = React.useRef<HTMLDivElement>(null);
  const [formatMenuOpen, setFormatMenuOpen] = React.useState(false);
  const [linkedPolicyId, setLinkedPolicyId] = React.useState(issue.linkedPolicyId || '');
  const [linkedPolicyNumber, setLinkedPolicyNumber] = React.useState(issue.linkedPolicyNumber || '');
  const [linkedInsuredId, setLinkedInsuredId] = React.useState(issue.linkedInsuredId || '');
  const [linkedInsuredName, setLinkedInsuredName] = React.useState(issue.linkedInsuredName || '');
  const [linkedAgentId, setLinkedAgentId] = React.useState(issue.linkedAgentId || '');
  const [linkedAgentName, setLinkedAgentName] = React.useState(issue.linkedAgentName || '');
  const [showAllLinks, setShowAllLinks] = React.useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = React.useState(false);
  const [linkPickerPos, setLinkPickerPos] = React.useState<{ top: number; left: number } | null>(null);
  const [linkQuery, setLinkQuery] = React.useState('');
  const [linkResults, setLinkResults] = React.useState<any[]>([]);
  const [linkLoading, setLinkLoading] = React.useState(false);
  const linkBtnRef = React.useRef<HTMLButtonElement>(null);
  const linkInputRef = React.useRef<HTMLInputElement>(null);
  const linkDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedMomentumIdsRef = React.useRef('');
  // Email draft editing + send state — keyed by "commentIndex-draftIndex"
  const [draftEdits, setDraftEdits] = React.useState<Record<string, { to: string; subject: string; body: string; cc: string }>>({});
  const [sentDrafts, setSentDrafts] = React.useState<Record<string, boolean>>({});
  const [sendingDraft, setSendingDraft] = React.useState<string | null>(null);
  // Collapsed state for email draft cards — only first is expanded by default
  const [expandedDrafts, setExpandedDrafts] = React.useState<Record<string, boolean>>({});
  // Inline validation / send-failure message per draft, and the draft awaiting
  // the "Send email?" confirmation (AI-drafted emails go straight to insureds).
  const [draftErrors, setDraftErrors] = React.useState<Record<string, string>>({});
  const [confirmDraft, setConfirmDraft] = React.useState<{ key: string; commentIndex: number; draftIndex: number; original: ParsedEmailDraft } | null>(null);
  // Discard closes the panel; confirm first when there's unsent text/files.
  const [discardConfirmOpen, setDiscardConfirmOpen] = React.useState(false);
  const sendMenuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const sendMenuRef = React.useRef<HTMLDivElement>(null);

  // Restore "Sent" badges from DB history when comments are available.
  // Caches the API response per task and re-matches whenever comments change
  // (parent can replace comments via polling or full-detail fetch).
  const emailHistoryCacheRef = React.useRef<{ taskId: string; records: { to: string; subject: string }[] }>({ taskId: '', records: [] });
  React.useEffect(() => {
    if (!comments.length) return;
    // "Sent" badges only exist on email-draft cards — no draft, nothing to restore,
    // so skip the email-history request.
    if (!comments.some(c => c.role.toLowerCase() !== 'human' && parseEmailDrafts(c.message).length > 0)) return;
    let cancelled = false;

    const matchAndRestore = (records: { to: string; subject: string }[]) => {
      if (!records.length || cancelled) return;
      const sentSet = new Set(
        records.map(h => `${stripEntityTokens(h.to).trim().toLowerCase()}|||${stripEntityTokens(h.subject).trim().toLowerCase()}`),
      );
      const restored: Record<string, boolean> = {};
      comments.forEach((c, ci) => {
        if (c.role === 'Human') return;
        const drafts = parseEmailDrafts(c.message);
        drafts.forEach((d, di) => {
          const key = `${d.to.trim().toLowerCase()}|||${d.subject.trim().toLowerCase()}`;
          if (sentSet.has(key)) restored[`${ci}-${di}`] = true;
        });
      });
      if (Object.keys(restored).length) setSentDrafts(prev => ({ ...prev, ...restored }));
    };

    if (emailHistoryCacheRef.current.taskId === issue.id) {
      // Already fetched for this task — just re-match against new comments
      matchAndRestore(emailHistoryCacheRef.current.records);
    } else {
      // First load for this task — fetch from API
      (async () => {
        const history = await fetchKanbanEmailHistory(issue.id);
        if (cancelled) return;
        const records = history.map(h => ({ to: h.to || '', subject: h.subject || '' }));
        emailHistoryCacheRef.current = { taskId: issue.id, records };
        matchAndRestore(records);
      })();
    }

    return () => { cancelled = true; };
  }, [issue.id, comments]);

  const { theme } = useTheme();
  const { runDetailPanel, setRunDetailPanel } = useRunDetailPanel();
  // Monotonic counter for View Details requests; late responses from an older click are dropped.
  const viewDetailsSeqRef = React.useRef(0);
  const { setSelectedInsuredId } = useInsuredDetail();
  const { setSelectedPolicyId } = usePolicyDetail();

  const linkComponents = React.useMemo(() => ({
    ...MD_HEADING_COMPONENTS,
    a: ({ href, children }: any) => {
      if (href?.startsWith('#INSURED_ID_')) {
        const id = href.replace('#INSURED_ID_', '');
        return <button className="insured-link" onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(id); }}>{children}</button>;
      }
      if (href?.startsWith('#POLICY_ID_')) {
        const id = href.replace('#POLICY_ID_', '');
        return <button className="insured-link" style={{ color: '#60a5fa' }} onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(id); }}>{children}</button>;
      }
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    },
  }), [setSelectedInsuredId, setSelectedPolicyId]);

  const {
    mentionActive, mentionQuery, mentionResults, mentionLoading, mentionActiveIdx,
    setMentionActiveIdx, onValueChange: mentionValueChange, onKeyDown: mentionKeyDown,
    selectMention, insertedNames, insertedEntities, clearInsertedNames,
  } = useInsuredMention();
  const commentBoxRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const isNewCommentRef = React.useRef(false);
  const hasScrolledOnOpenRef = React.useRef(false);
  // Guards the title/description resync below from clobbering an in-progress
  // keystroke when the `issue` prop updates mid-edit (live polling, or the
  // background full-detail fetch backfilling the rest of the comment thread).
  const editingFieldRef = React.useRef({ title: false, description: false });
  const mirrorRef = React.useRef<HTMLDivElement>(null);
  const feedRef = React.useRef<HTMLDivElement>(null);
  const [mentionPos, setMentionPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const [mentionExpanded, setMentionExpanded] = React.useState(false);
  const [isRecording, setIsRecording] = React.useState(false);
  const recognitionRef = React.useRef<any>(null);
  const [titleError, setTitleError] = React.useState('');
  const [apiError, setApiError] = React.useState('');
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [fileDragOver, setFileDragOver] = React.useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = React.useState(false);
  const [showAmsSelector, setShowAmsSelector] = React.useState(false);
  const [resolvingFileName, setResolvingFileName] = React.useState<string | null>(null);
  // Shows an uploaded file's content in an in-panel drawer — not a new tab, not a modal popup.
  const [previewFile, setPreviewFile] = React.useState<{ name: string; url: string } | null>(null);
  const [uploadError, setUploadError] = React.useState('');
  const [pendingUploads, setPendingUploads] = React.useState<Array<{ id: string; name: string; size?: number }>>([]);
  const [uploadedFiles, setUploadedFiles] = React.useState<Array<{ name: string; url?: string; size?: number; transactionId: string }>>([]);
  const [commentAttachedFiles, setCommentAttachedFiles] = React.useState<Array<{ name: string; url?: string; size?: number; transactionId: string }>>([]);
  // Collapses the System entry's empty description box until the user asks for it.
  const [showDescriptionEditor, setShowDescriptionEditor] = React.useState(false);
  const descTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Auto-size the System description box for short content, capped at 150px.
  React.useLayoutEffect(() => {
    const el = descTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 150)}px`;
  }, [description, mode, showDescriptionEditor]);

  // Comment composer: keep one-line state aligned with action controls, then
  // grow with content up to a capped height.
  const COMPOSER_CONTROL_PX = 36;
  const COMMENT_BOX_MIN_PX = COMPOSER_CONTROL_PX;
  const COMMENT_BOX_MAX_PX = 104;
  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(COMMENT_BOX_MIN_PX, Math.min(el.scrollHeight, COMMENT_BOX_MAX_PX))}px`;
  }, [newComment]);
  const [showAllUploadedFiles, setShowAllUploadedFiles] = React.useState(false);
  const [hasUploadedOverflow, setHasUploadedOverflow] = React.useState(false);
  const uploadedFilesWrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    const el = uploadedFilesWrapRef.current;
    if (!el || showAllUploadedFiles) return;
    const check = () => setHasUploadedOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    // Defer the observer's own check to the next frame — running it
    // synchronously inside the ResizeObserver callback (which itself can
    // trigger further layout, e.g. from the comment box's height effect
    // above) is what causes the browser's "ResizeObserver loop completed
    // with undelivered notifications" warning.
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(check);
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, [commentAttachedFiles, pendingUploads, showAllUploadedFiles]);
  const [streamText, setStreamText] = React.useState('');
  // Backend go-ahead decision from the run's terminal answer event — a prompt
  // (render Yes / No under the newest AI note), null for "no buttons", or
  // undefined when no run in this panel has decided yet (fall back to the text
  // heuristics). Reset when the user replies or the panel switches task.
  const [aiProceedPrompt, setAiProceedPrompt] = React.useState<AiQuickPrompt | null | undefined>(undefined);
  const [thoughtText, setThoughtText] = React.useState('');
  const [currentTool, setCurrentTool] = React.useState('');
  const [isThinking, setIsThinking] = React.useState(false);
  const [streamingContent, setStreamingContentNow] = React.useState('');
  // Stream chunks arrive far faster than the screen refreshes, and each one used
  // to re-render this whole panel and re-parse the accumulated answer. Chunks go
  // through queueStreamingContent (at most one commit per animation frame);
  // direct sets (clearing, final text) cancel any queued frame so a stale chunk
  // can never land after them.
  const streamFrameRef = React.useRef<number | null>(null);
  const pendingStreamRef = React.useRef('');
  const setStreamingContent = React.useCallback((value: string) => {
    if (streamFrameRef.current != null) {
      cancelAnimationFrame(streamFrameRef.current);
      streamFrameRef.current = null;
    }
    setStreamingContentNow(value);
  }, []);
  const queueStreamingContent = React.useCallback((value: string) => {
    pendingStreamRef.current = value;
    if (streamFrameRef.current != null) return;
    streamFrameRef.current = requestAnimationFrame(() => {
      streamFrameRef.current = null;
      setStreamingContentNow(pendingStreamRef.current);
    });
  }, []);
  React.useEffect(() => () => {
    if (streamFrameRef.current != null) cancelAnimationFrame(streamFrameRef.current);
  }, []);
  // Set when an agent run ends without an answer — the terminal event carried
  // error_message (e.g. BUDGET_REACHED: the task's AI session hit its spending
  // budget and was paused). Rendered as a persistent notice in the Notes feed
  // rather than the auto-dismissing apiError toast, so the user actually reads
  // why nothing came back. Cleared when the next run (or re-attach) starts.
  const [aiRunError, setAiRunError] = React.useState<KanbanRunError | null>(null);
  // Detects a stall mid-stream (some of the answer arrived, but the next chunk
  // is taking a while) the same way the chat page's ChatContainer does — track
  // when content last changed, then poll for staleness — rather than touching
  // every setStreamingContent call site with its own timestamp bookkeeping.
  const lastStreamChunkAtRef = React.useRef(0);
  React.useEffect(() => {
    lastStreamChunkAtRef.current = Date.now();
  }, [streamingContent]);
  const [showStreamGapIndicator, setShowStreamGapIndicator] = React.useState(false);
  // Keyed on whether a stream is active, not on its content, so the 250ms
  // ticker isn't torn down and recreated for every chunk.
  const isStreaming = !!streamingContent;
  React.useEffect(() => {
    if (!isStreaming) { setShowStreamGapIndicator(false); return; }
    const tick = () => {
      const staleMs = Date.now() - lastStreamChunkAtRef.current;
      setShowStreamGapIndicator(!isThinking && staleMs >= 1200);
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [isStreaming, isThinking]);
  const streamAbortRef = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => { streamAbortRef.current?.abort(); }, []);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragDepthRef = React.useRef(0);
  const autoRunKeyRef = React.useRef<string>('');
  const transactionIdRef = React.useRef<string>('');
  const prevIssueIdRef = React.useRef<string>(issue.id);

  const readFilesFromDataTransfer = React.useCallback((dt: DataTransfer | null): File[] => {
    if (!dt) return [];
    if (dt.files && dt.files.length) return Array.from(dt.files).filter(f => !!f);
    const out: File[] = [];
    Array.from(dt.items || []).forEach((it) => {
      if (it.kind !== 'file') return;
      const f = it.getAsFile();
      if (f) out.push(f);
    });
    return out;
  }, []);

  const hasDragItems = React.useCallback((dt: DataTransfer | null): boolean => {
    if (!dt) return false;
    if (dt.items && dt.items.length > 0) return true;
    if (dt.files && dt.files.length > 0) return true;
    return false;
  }, []);


  // Sync internal state when the issue prop updates from live polling
  React.useEffect(() => {
    // Don't stomp a title/description the user is actively typing — the prop
    // can update mid-edit from polling or the background full-detail fetch.
    if (!editingFieldRef.current.title) setTitle(issue.title);
    if (!editingFieldRef.current.description) setDescription(issue.description || '');
    setStatus(issue.status);
    setComments(issue.comments ?? []);
    setLinkedPolicyId(issue.linkedPolicyId || '');
    setLinkedPolicyNumber(issue.linkedPolicyNumber || '');
    setLinkedInsuredId(issue.linkedInsuredId || '');
    setLinkedInsuredName(issue.linkedInsuredName || '');
    setLinkedAgentId(issue.linkedAgentId || '');
    setLinkedAgentName(issue.linkedAgentName || '');
  // Comparing full serialized content (not just length) so a background full-detail
  // fetch that enriches existing comments in place — e.g. backfilling id/isLiked/
  // isDisliked/feedback once the summary-only fetch is replaced — actually re-syncs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.id, issue.title, issue.description, issue.status, JSON.stringify(issue.comments)]);

  const statusMountRef = React.useRef(true);
  React.useEffect(() => {
    if (statusMountRef.current) { statusMountRef.current = false; return; }
    if (feedRef.current) feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
  }, [status]);

  React.useEffect(() => {
    if (!apiError) return;
    const t = setTimeout(() => setApiError(''), 4500);
    return () => clearTimeout(t);
  }, [apiError]);

  React.useEffect(() => {
    // Seed immediately from localStorage cache (populated by MomentumCommandPage / DigitalWorkforcePage)
    try {
      const s = localStorage.getItem('momentum_digital_workforce_agents');
      if (s) {
        const raw = JSON.parse(s) as any[];
        const cached = raw
          .filter(a => typeof a?.id === 'string' && typeof a?.name === 'string' && a.name.trim())
          .map(a => ({ id: a.id as string, name: (a.name as string).trim(), isPrimary: !!a.isPrimary }));
        if (cached.length > 0) setDwAgents(cached);
      }
    } catch {}
    // Always fetch fresh from API and update both state and localStorage cache
    listDwAgents(false, true)
      .then(records => {
        const active = records.filter(r => !r.is_archived);
        setDwAgents(active.map(r => ({ id: r.agent_id, name: r.name, isPrimary: !!r.is_primary_agent, isSystem: !!r.is_system })));
        try {
          localStorage.setItem(
            'momentum_digital_workforce_agents',
            JSON.stringify(active.map(r => ({ id: r.agent_id, name: r.name, isPrimary: !!r.is_primary_agent })))
          );
        } catch {}
      })
      .catch(() => {});
  }, []);

  // New commands default to the primary Digital Workforce agent instead of "User",
  // once the agent list has loaded — but only until the user actively picks something else.
  React.useEffect(() => {
    if (mode !== 'create' || userChangedAssigneeRef.current || assigneeAgentId || dwAgents.length === 0) return;
    const primary = dwAgents.find(a => a.isPrimary) || dwAgents[0];
    setAssigneeType(2);
    setAssigneeAgentId(primary.id);
    setAssigneeAgentName(primary.name);
  }, [mode, dwAgents, assigneeAgentId]);

  const applyFormat = (prefix: string, suffix: string = prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = newComment.slice(start, end);
    const placeholder = selected || 'text';
    const next = `${newComment.slice(0, start)}${prefix}${placeholder}${suffix}${newComment.slice(end)}`;
    setNewComment(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length + placeholder.length);
    }, 0);
  };

  React.useEffect(() => {
    if (mentionActive && textareaRef.current) {
      const rect = textareaRef.current.getBoundingClientRect();
      setMentionPos({ top: rect.top, left: rect.left, width: rect.width });
    } else {
      setMentionPos(null);
    }
  }, [mentionActive]);

  React.useEffect(() => {
    setTitle(issue.title);
    setDescription(issue.description || '');
    setStatus(issue.status);
    setSelectedCategoryId(issue.categoryId ?? categoryId ?? 7);
    setComments(issue.comments ?? []);
    setNewComment('');
    setLinkedPolicyId(issue.linkedPolicyId || '');
    setLinkedPolicyNumber(issue.linkedPolicyNumber || '');
    setLinkedInsuredId(issue.linkedInsuredId || '');
    setLinkedInsuredName(issue.linkedInsuredName || '');
    setLinkedAgentId(issue.linkedAgentId || '');
    setLinkedAgentName(issue.linkedAgentName || '');
    setCommentAttachedFiles([]);
    setPendingUploads([]);
    setShowAllUploadedFiles(false);
    setUploadError('');
    setShowDescriptionEditor(false);
    resolvedMomentumIdsRef.current = '';
    setAiProceedPrompt(undefined);
  }, [issue.id]);

  // Separate from the mount effect above: `issue.agentName`/`agentId`/`assigneeType`
  // often arrive after the panel first opens (the board/list card is shown before
  // the background fetchTaskDetail call resolves and merges in the authoritative
  // assignee), and that later value lands with the same `issue.id` — so this needs
  // its own dependency on the assignee fields to actually pick up the correction.
  React.useEffect(() => {
    // assigneeType from the API remains the source of truth, but agent metadata
    // should not be wiped when non-DW assignee types arrive without agent fields.
    const apiAssigneeType = issue.assigneeType ?? 0;
    const nextAgentName = normalizeAgentName(issue.agentName);
    const nextAgentId = issue.agentId || '';
    setAssigneeType(apiAssigneeType);
    setAssigneeAgentName((prev) => nextAgentName || prev);
    setAssigneeAgentId((prev) => nextAgentId || prev);
  }, [issue.id, issue.agentName, issue.agentId, issue.assigneeType]);

  // Separate from the mount effect above: `issue.files` often arrives after the
  // panel first opens (the board/list card is shown before the background
  // fetchTaskDetail call resolves), and that later value is a new object each
  // time even when `issue.id` hasn't changed — so this needs its own dependency
  // on `issue.files` to actually pick up files that land after initial open.
  React.useEffect(() => {
    const toFileRows = (raw: any): Array<{ name: string; url?: string; size?: number }> => {
      if (!raw) return [];
      const parsed = Array.isArray(raw)
        ? raw
        : (typeof raw === 'string'
            ? (() => {
                try { return JSON.parse(raw); } catch { return []; }
              })()
            : []);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((f: any) => {
          if (typeof f === 'string') return { name: f };
          const name = String(f?.name ?? f?.Name ?? '').trim();
          if (!name) return null;
          const urlRaw = f?.url ?? f?.Url;
          const sizeRaw = f?.size ?? f?.Size;
          return {
            name,
            url: typeof urlRaw === 'string' && urlRaw ? urlRaw : undefined,
            size: typeof sizeRaw === 'number' && Number.isFinite(sizeRaw) ? sizeRaw : undefined,
          };
        })
        .filter((f: { name: string } | null): f is { name: string; url?: string; size?: number } => !!f && !!f.name);
    };

    // De-dupe by name — the backend can surface the same filename twice
    // (explicit file-name list + transactionId-linked blob storage).
    const seenFileNames = new Set<string>();
    setUploadedFiles(toFileRows(issue.files)
      .filter((f) => {
        const key = f.name.toLowerCase();
        if (seenFileNames.has(key)) return false;
        seenFileNames.add(key);
        return true;
      })
      .map((f, i) => ({
        name: f.name,
        url: f.url,
        size: (f as any).size,
        transactionId: `saved-${i}-${f.name}`,
      })));
  }, [issue.id, issue.files]);

  React.useEffect(() => {
    const prevId = prevIssueIdRef.current;
    const nextId = issue.id;
    const isNewToSaved = (prevId === '__new__' || !prevId) && !!nextId && nextId !== '__new__';
    // Saved tasks carry their transactionId from the GET API — reuse it so file
    // uploads and upserts stay linked to the task's original transaction.
    if (!isNewToSaved) transactionIdRef.current = issue.transactionId || '';
    prevIssueIdRef.current = nextId;
  }, [issue.id, issue.transactionId]);

  // Header file chips (Back-to-Commands row) dispatch preview requests from the
  // parent page. Route them into the same right-side drawer used everywhere else.
  // Must tell the parent once consumed — otherwise its request state stays
  // "stuck" non-null, and the next task's panel (a fresh mount, so this effect
  // runs on mount regardless of the dependency array) would immediately
  // re-open the same stale file every time.
  React.useEffect(() => {
    if (!previewFileRequest?.url || !previewFileRequest?.name) return;
    setPreviewFile({ name: previewFileRequest.name, url: previewFileRequest.url });
    onPreviewFileRequestConsumed?.();
  }, [previewFileRequest?.key, previewFileRequest?.name, previewFileRequest?.url, onPreviewFileRequestConsumed]);

  React.useEffect(() => {
    if (!feedRef.current) return;
    // Also jump straight to the latest message the first time this panel
    // renders a thread, so opening a task shows its most recent activity
    // instead of the top of a possibly long conversation.
    if (isNewCommentRef.current || !hasScrolledOnOpenRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
      isNewCommentRef.current = false;
      hasScrolledOnOpenRef.current = true;
    }
  }, [comments]);

  React.useEffect(() => {
    if (linkDebounceRef.current) clearTimeout(linkDebounceRef.current);
    if (linkQuery.length >= 2) {
      linkDebounceRef.current = setTimeout(async () => {
        setLinkLoading(true);
        try {
          const authToken = localStorage.getItem('authToken') || '';
          const amsToken = localStorage.getItem('amsToken') || '';
          const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/search-insureds`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ searchText: linkQuery, nc_token: amsToken }),
          });
          const data = await res.json();
          setLinkResults(Array.isArray(data) ? data : (data.insureds || []));
        } catch { setLinkResults([]); } finally { setLinkLoading(false); }
      }, 280);
    } else {
      setLinkResults([]);
    }
    return () => { if (linkDebounceRef.current) clearTimeout(linkDebounceRef.current); };
  }, [linkQuery]);

  // Auto-populate linked entities from <!--MOMENTUM_IDS:{...}--> in AI comments
  React.useEffect(() => {
    for (let i = comments.length - 1; i >= 0; i--) {
      const c = comments[i];
      if (c.role.toLowerCase() === 'human') continue;
      const m = MOMENTUM_IDS_RE.exec(c.message);
      if (!m) continue;
      try {
        const ids = JSON.parse(m[1]) as { insured_id?: string; policy_id?: string; prospect_id?: string };
        const insuredId = ids.insured_id || ids.prospect_id || '';
        if (ids.prospect_id && !ids.insured_id) seedInsuredKind(ids.prospect_id, 'prospect');
        const policyId = ids.policy_id || '';
        if (!insuredId && !policyId) break;
        const key = `${insuredId}|${policyId}`;
        if (resolvedMomentumIdsRef.current === key) break;
        resolvedMomentumIdsRef.current = key;
        // Only look up what isn't linked yet: the insured record comes from the
        // shared per-session cache (same request the Linked chips use), and the
        // policy list is fetched only when there's a policy id to resolve.
        const needInsured = !!insuredId && insuredId !== linkedInsuredId;
        const needPolicy = !!policyId && !!insuredId && policyId !== linkedPolicyId;
        if (!needInsured && !needPolicy) break;
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        const headers = { Authorization: `Bearer ${authToken}` };
        Promise.all([
          needInsured ? fetchInsuredRecord(insuredId) : Promise.resolve(null),
          needPolicy
            ? fetch(`${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insuredpolicies?insuredId=${encodeURIComponent(insuredId)}&nowCertsToken=${encodeURIComponent(amsToken)}`, { headers }).then(r => r.ok ? r.json() : []).catch(() => [])
            : Promise.resolve([]),
        ]).then(([insuredData, policiesData]) => {
          if (needInsured && insuredData) {
            const item = Array.isArray(insuredData) ? insuredData[0] : insuredData;
            if (item) {
              const name = item.commercialName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || insuredId;
              setLinkedInsuredId(insuredId);
              setLinkedInsuredName(name);
            }
          }
          if (needPolicy && Array.isArray(policiesData)) {
            const pol = policiesData.find((p: any) => p.databaseId === policyId);
            setLinkedPolicyId(policyId);
            setLinkedPolicyNumber(pol?.number || policyId);
          }
        });
      } catch { /* ignore malformed */ }
      break;
    }
  }, [comments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect all $$IDF_name##id$$ and $$PDF_name##id$$ tokens from every comment.
  // These are shown as clickable chips in the LINKED section (deduped against existing linked IDs).
  const commentLinks = React.useMemo(() => {
    const seen = new Set<string>();
    const links: { type: 'IDF' | 'PDF'; name: string; id: string }[] = [];
    for (const c of comments) {
      const re = /\$\$(IDF|PDF)_([^#$]+)##([^$]+)\$\$/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(c.message)) !== null) {
        const [, type, name, id] = m;
        const key = `${type}:${id.trim()}`;
        if (!seen.has(key)) {
          seen.add(key);
          links.push({ type: type as 'IDF' | 'PDF', name: name.trim(), id: id.trim() });
        }
      }
    }
    return links;
  }, [comments]);

  // Insured vs prospect for the Linked row. @-mentions already know the kind
  // (the search result's typeName), so seed those; the rest are looked up.
  React.useEffect(() => {
    insertedEntities.forEach((entity) => {
      const t = (entity.typeName || '').toLowerCase();
      if (t === 'prospect') seedInsuredKind(entity.id, 'prospect');
      else if (t === 'insured') seedInsuredKind(entity.id, 'insured');
    });
  }, [insertedEntities]);
  const insuredKinds = useInsuredKinds([
    ...(linkedInsuredId ? [linkedInsuredId] : []),
    ...commentLinks.filter(l => l.type === 'IDF').map(l => l.id),
  ]);

  // Replaces @Name mentions with $$IDF_Name##id$$ / $$PDF_Name##id$$ annotations before saving
  const annotateComment = React.useCallback((text: string): string => annotateWith(text, insertedEntities), [insertedEntities]);

  const addComment = React.useCallback(() => {
    const text = newComment.trim();
    if (!text) return;
    isNewCommentRef.current = true;
    setComments(prev => [...prev, { role: 'Human', message: annotateComment(text), created: new Date().toISOString(), ...operatorIdentity() }]);
    setNewComment('');
    clearInsertedNames();
  }, [newComment, annotateComment, clearInsertedNames]);

  const toggleMic = React.useCallback(() => {
    const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRec) return;
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }
    const rec = new SpeechRec();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e: any) => {
      const transcript = Array.from(e.results as SpeechRecognitionResultList)
        .map((r: any) => r[0].transcript)
        .join(' ')
        .trim();
      if (transcript) {
        setNewComment(prev => {
          const updated = prev ? `${prev} ${transcript}` : transcript;
          mentionValueChange(updated);
          return updated;
        });
      }
    };
    rec.onend = () => setIsRecording(false);
    rec.onerror = () => setIsRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setIsRecording(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [isRecording, mentionValueChange]);

// Streams an agent's run inline (thinking block + live answer) into the Notes
// feed, then reconciles with the persisted task once the stream finishes.
// Shared by handleSave's post-send flow and any other action that hands a
// task to an agent (e.g. a manual status change into In Progress) so both
// get the same visible thought process instead of a silent background call.
const runInlineAgentStream = React.useCallback((taskId: string, promptText: string, baseIssue: CommandIssue) => {
  streamAbortRef.current?.abort();
  const ctrl = new AbortController();
  streamAbortRef.current = ctrl;
  setThoughtText('');
  setCurrentTool('');
  setStreamingContent('');
  setAiRunError(null);
  setIsThinking(true);
  const mergeLatestAiComment = (arr: CommentModel[], answer: string): CommentModel[] => {
    if (!answer) return arr;
    // AI comments carry the identity of the operator whose run produced them —
    // matching what the backend stamps from the JWT at finalize time, so a
    // later panel save doesn't wipe the persisted values.
    if (!arr.length) return [{ role: 'AI', message: answer, created: new Date().toISOString(), ...operatorIdentity() }];
    const out = [...arr];
    const last = out[out.length - 1];
    const lastRole = (last.role ?? '').toLowerCase();
    const isLastAi = lastRole.includes('ai') || lastRole === 'assistant';
    if (isLastAi) {
      out[out.length - 1] = { ...last, role: 'AI', message: answer, created: last.created ?? new Date().toISOString() };
    } else {
      out.push({ role: 'AI', message: answer, created: new Date().toISOString(), ...operatorIdentity() });
    }
    return out;
  };
  (async () => {
    const aToken = localStorage.getItem('authToken') || '';
    const ncToken = localStorage.getItem('amsToken') || '';
    let accAnswer = '';
    const thinkingStartedAt = Date.now();
    const MIN_THINKING_VISIBLE_MS = 700;
    const MIN_THOUGHT_VISIBLE_MS = 900;
    let sawThought = false;
    let lastThoughtAt = 0;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const ensureThinkingVisible = async () => {
      const elapsed = Date.now() - thinkingStartedAt;
      if (elapsed < MIN_THINKING_VISIBLE_MS) {
        await wait(MIN_THINKING_VISIBLE_MS - elapsed);
      }
    };
    const ensureThoughtVisible = async () => {
      if (!sawThought || !lastThoughtAt) return;
      const elapsed = Date.now() - lastThoughtAt;
      if (elapsed < MIN_THOUGHT_VISIBLE_MS) {
        await wait(MIN_THOUGHT_VISIBLE_MS - elapsed);
      }
    };
    let answerTurnStale = false;
    // Failure reported on the terminal answer event (see kanbanRunError). A ref
    // rather than a `let`: it is assigned inside the SSE callback and read after
    // the await, which TS's narrowing would otherwise type as never-assigned.
    const runError: { current: KanbanRunError | null } = { current: null };
    try {
      const fd = new FormData();
      fd.append('taskId', taskId);
      fd.append('nc_token', ncToken);
      fd.append('prompt', promptText);
      const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || '')}/api/ams/momentum-kanban-stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${aToken}` },
        body: fd,
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        let msg = `AI stream failed (${res.status})`;
        try {
          const b = await res.json();
          msg = b?.errorMessage || b?.message || msg;
        } catch {
          try {
            const t = await res.text();
            if (t) msg = t;
          } catch {
            // Keep fallback message.
          }
        }
        setApiError(msg);
        setIsThinking(false);
        return;
      }
      await consumeKanbanSse(res.body, ctrl.signal, (parsed) => {
        const isDone = parsed.responseStreaming === false || parsed.ResponseLoading === false;
        if (parsed.type === 'thought') {
          if ((parsed.thought || parsed.currentTool) && accAnswer) answerTurnStale = true;
          if (parsed.thought) {
            sawThought = true;
            lastThoughtAt = Date.now();
            setThoughtText(prev => prev + parsed.thought);
          }
          if (parsed.currentTool) setCurrentTool(parsed.currentTool);
          if (isDone) {
            setStreamText(accAnswer);
            setStreamingContent('');
            return 'stop';
          }
        } else if (parsed.type === 'answer') {
          const runErr = kanbanRunError(parsed);
          if (runErr) runError.current = runErr;
          const text = parsed.response ?? '';
          if (text) {
            if (answerTurnStale) {
              accAnswer = '';
              answerTurnStale = false;
            }
            accAnswer = foldAnswerChunk(accAnswer, text);
            queueStreamingContent(accAnswer);
          }
          if (isDone) {
            // Backend go-ahead decision rides on the terminal answer event.
            setAiProceedPrompt(proceedPromptFromEvent(parsed));
            setStreamText(accAnswer);
            setStreamingContent('');
            return 'stop';
          }
        } else {
          // legacy format fallback
          const chunk = parsed.Answer ?? parsed.response ?? parsed.content ?? parsed.text ?? '';
          if (chunk) {
            if (answerTurnStale) {
              accAnswer = '';
              answerTurnStale = false;
            }
            accAnswer = foldAnswerChunk(accAnswer, chunk);
            queueStreamingContent(accAnswer);
          }
          if (isDone) {
            setStreamText(accAnswer);
            setStreamingContent('');
            return 'stop';
          }
        }
      });
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      if (!accAnswer) setStreamText('Error fetching AI response.');
    } finally {
      await ensureThoughtVisible();
      await ensureThinkingVisible();
      // Hide the thought process once the response has arrived.
      if (accAnswer) {
        setThoughtText('');
        setCurrentTool('');
      }
      setIsThinking(false);
      if (accAnswer) setStreamText(accAnswer);
      setStreamingContent('');
      if (runError.current && !ctrl.signal.aborted) setAiRunError(runError.current);
      // Ensure AI answer is visible immediately in Notes even if task API is slow/empty.
      if (accAnswer && !ctrl.signal.aborted) {
        const optimisticComments = mergeLatestAiComment(baseIssue.comments ?? [], accAnswer);
        setComments(optimisticComments);
        const optimisticIssue: CommandIssue = { ...baseIssue, comments: optimisticComments };
        if (onSaveSilent) onSaveSilent(optimisticIssue); else onSave(optimisticIssue);
      }
      // Stream done — fetch fresh task, then hide the transient AI response bubble
      // so the Notes thread is the visible source after task API returns.
      if (accAnswer && !ctrl.signal.aborted) {
        try {
          const tRes = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/tasks`, {
            headers: { Authorization: `Bearer ${aToken}` },
          });
          if (tRes.ok) {
            const tJson = await tRes.json();
            const tList: any[] = Array.isArray(tJson) ? tJson : (tJson?.data ?? []);
            const t = tList.find((item: any) => String(item.id) === taskId);
            if (t) {
              // The tasks-list endpoint doesn't return the full comment thread — only
              // the newest one, as lastCommentData (or lastComment, its JSON-string
              // twin). That's enough to learn the real id the backend assigned to the
              // AI reply we just streamed; the rest of the thread stays locally tracked.
              const parseLatestComment = (task: any): CommentModel | null => {
                const raw = task?.lastCommentData ?? (() => {
                  if (typeof task?.lastComment !== 'string') return null;
                  try { return JSON.parse(task.lastComment); } catch { return null; }
                })();
                if (!raw) return null;
                return {
                  id: raw.id ?? raw.Id ?? null,
                  role: raw.role ?? raw.Role ?? 'Human',
                  message: raw.message ?? raw.Message ?? '',
                  created: raw.created ?? raw.Created,
                  isLiked: raw.isLiked ?? raw.IsLiked ?? false,
                  isDisliked: raw.isDisliked ?? raw.IsDisliked ?? false,
                  feedback: raw.feedback ?? raw.Feedback ?? '',
                  userId: raw.userId ?? raw.UserId ?? '',
                  firstName: raw.firstName ?? raw.FirstName ?? '',
                  lastName: raw.lastName ?? raw.LastName ?? '',
                };
              };
              const SMAP: Record<string, CommandStatus> = {
                '11111111-1111-1111-1111-111111111111': 'todo',
                '22222222-2222-2222-2222-222222222222': 'inprogress',
                '33333333-3333-3333-3333-333333333333': 'inreview',
                '44444444-4444-4444-4444-444444444444': 'done',
              };
              // Stream text is authoritative for what the user just received — only
              // the id is borrowed from the task API's latest-comment snapshot.
              const mergedComments = accAnswer
                ? mergeLatestAiComment(baseIssue.comments ?? [], accAnswer)
                : (baseIssue.comments ?? []);
              const latestPersisted = parseLatestComment(t);
              const finalComments = (() => {
                if (!latestPersisted?.id || !mergedComments.length) return mergedComments;
                const lastIdx = mergedComments.length - 1;
                const last = mergedComments[lastIdx];
                if (last.id || !(last.role ?? '').toLowerCase().includes('ai')) return mergedComments;
                const out = [...mergedComments];
                out[lastIdx] = { ...last, id: latestPersisted.id };
                return out;
              })();
              // assigneeType from the API is the source of truth. agentName is only
              // trusted (or carried over from a brief omission right after upsert)
              // while assigneeType actually says "DW agent" (2) — otherwise a
              // leftover agentName on the record (e.g. the API resetting assigneeType
              // to 0 once the AI hands the task back) must not resurrect the old
              // agent selection, which is exactly what forced it back to 2 before.
              const rawAssigneeType = t.assigneeType ?? t.assignee_type;
              const mergedAssigneeType = rawAssigneeType ?? baseIssue.assigneeType;
              const nextAgentName = mergedAssigneeType === 2
                ? normalizeAgentName(t.agentName ?? t.agent_name ?? t.AgentName ?? t.agent?.name)
                : '';
              const priorAgentName = mergedAssigneeType === 2 ? normalizeAgentName(baseIssue.agentName) : '';
              const mergedAgentName = nextAgentName || priorAgentName || undefined;
              const nextAgentId = t.agentId ?? t.agent_id ?? t.AgentId ?? t.agent?.id;
              const mergedAgentId = nextAgentName ? (nextAgentId || undefined) : (priorAgentName ? baseIssue.agentId : undefined);
              const freshIssue: CommandIssue = {
                ...baseIssue,
                id: String(t.id),
                title: t.title || baseIssue.title,
                description: t.description || baseIssue.description,
                status: SMAP[t.statusId] ?? baseIssue.status,
                comments: finalComments,
                assigneeType: mergedAssigneeType,
                lastAssigneeType: (() => {
                  const raw = t.lastAssigneeType ?? t.last_assignee_type;
                  const n = typeof raw === 'number' ? raw : Number(raw);
                  if (Number.isFinite(n)) return n === 3 ? 2 : n;
                  return baseIssue.lastAssigneeType ?? mergedAssigneeType;
                })(),
                agentName: mergedAgentName,
                agentId: mergedAgentId,
              };
              if (onSaveSilent) onSaveSilent(freshIssue); else onSave(freshIssue);

              // The AI comment above sometimes lands in the task API before the
              // backend has finished persisting it, so it comes back without a
              // real id — and the like/dislike/feedback icons stay hidden (they
              // require an id to target the feedback endpoint). Poll a few more
              // times in the background to backfill just that id once it exists,
              // without disturbing anything else already on screen.
              const lastComment = finalComments[finalComments.length - 1];
              if (lastComment && !lastComment.id && (lastComment.role ?? '').toLowerCase().includes('ai')) {
                (async () => {
                  for (let attempt = 0; attempt < 4 && !ctrl.signal.aborted; attempt++) {
                    await new Promise(r => setTimeout(r, 1500));
                    try {
                      const retryRes = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/tasks`, {
                        headers: { Authorization: `Bearer ${aToken}` },
                      });
                      if (!retryRes.ok) continue;
                      const retryJson = await retryRes.json();
                      const retryList: any[] = Array.isArray(retryJson) ? retryJson : (retryJson?.data ?? []);
                      const retryTask = retryList.find((item: any) => String(item.id) === taskId);
                      if (!retryTask) continue;
                      const retryLatest = parseLatestComment(retryTask);
                      if (retryLatest?.id && retryLatest.message === lastComment.message) {
                        const foundId = retryLatest.id;
                        setComments(prev => prev.map(c => (c.message === lastComment.message && !c.id) ? { ...c, id: foundId } : c));
                        break;
                      }
                    } catch {}
                  }
                })();
              }
            }
          }
        } catch {}
        // Task API call completed (success/fail/not-found): hide transient AI bubble.
        setStreamText('');
      }
      setApiBusy(false);
    }
  })();
}, [onSave, onSaveSilent, queueStreamingContent, setStreamingContent]);

const handleSave = React.useCallback(async (commentsOverride?: CommentModel[], overrideStatus?: CommandStatus, overrideAssigneeType?: number, closeAfter?: boolean, triggerAi?: boolean, overrideAgentName?: string, overrideAgentId?: string, promptOverride?: string) => {
  // Starting a new send should reset any previous stream/thought UI so old Q/A does not linger.
  streamAbortRef.current?.abort();
  setThoughtText('');
  setCurrentTool('');
  setStreamingContent('');
  setStreamText('');
  setAiProceedPrompt(undefined);
  setIsThinking(false);

    if (!title.trim()) {
      setTitleError('Title is required');
      return;
    }
    setTitleError('');
    setApiError('');

    const toSave = commentsOverride ?? comments;
    const effectiveStatus = overrideStatus ?? status;
    const effectiveAssigneeType = overrideAssigneeType ?? assigneeType;
    const preferredAgentName = (overrideAgentName ?? assigneeAgentName).trim();
    // agent_name/agent_id identify the Digital Workforce agent that works the
    // task. A task owned by a human simply has none -- never the placeholder
    // 'User', and never the assigneeId stuffed into the agent id column, both of
    // which used to be written here and destroyed the real attribution.
    const effectiveAgentName = preferredAgentName || normalizeAgentName(issue.agentName);
    const effectiveAgentId = effectiveAgentName
      ? (
          overrideAgentId ||
          assigneeAgentId ||
          issue.agentId ||
          dwAgents.find(a => a.name.trim().toLowerCase() === effectiveAgentName.toLowerCase())?.id ||
          ''
        )
      : '';
    setApiBusy(true);
    setSaving(true);
    const isNew = !issue.id || issue.id === '__new__';
    const authToken = localStorage.getItem('authToken') || '';
    if (isNew && !transactionIdRef.current) {
      transactionIdRef.current = createKanbanTransactionId();
    }
    // Also present in edit mode when a file was uploaded (upload seeds the ref),
    // so the backend can link the uploaded blobs to this task.
    const transactionId = transactionIdRef.current || undefined;
    let resolvedId = issue.id;
    // Task number (e.g. "CMD-111") is backend-assigned, never generated on the
    // UI — for a new task we send null and adopt whatever the server returns.
    let resolvedCode = issue.code;
    let saveSucceeded = false;
    try {
      // Upsert from handleSave is intentionally create-only. Existing tasks are
      // persisted via dedicated manual handlers (status/assignee) to avoid
      // background upsert calls from reply/send/autorun flows.
      if (isNew) {
        const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/task/upsert`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            ...(transactionId ? { 'X-Transaction-Id': transactionId } : {}),
          },
          body: JSON.stringify({
            id: 0,
            taskNumber: null,
            statusId: KANBAN_STATUS_IDS[effectiveStatus],
            title,
            description,
            comments: toCommentsJson(toSave),
            assigneeId: issue.assigneeId ?? 0,
            // API contract: 0 = User, 1 = AI Agent, 2 = others (DW agents; 3 is the
            // panel-internal DW marker and must never reach the API).
            assigneeType: effectiveAssigneeType === 3 ? 2 : effectiveAssigneeType,
            lastAssigneeType: effectiveAssigneeType === 3 ? 2 : effectiveAssigneeType,
            position,
            source,
            categoryId: selectedCategoryId,
            // Preserve agent metadata in payload; avoid nulls for compatibility
            // with downstream flows that expect stable string values.
            agentName: effectiveAgentName || '',
            agentId: effectiveAgentId || '',
            // Stringified array of file names only (no urls) per API contract; omit (null)
            // when empty so the backend preserves existing files.
            files: uploadedFiles.length
              ? JSON.stringify(uploadedFiles.map((f) => f.name))
              : null,
            transactionId,
          }),
        });
        if (!res.ok) {
          let msg = `Request failed (${res.status})`;
          try { const b = await res.json(); msg = b.errorMessage || b.message || msg; } catch {}
          setApiError(msg);
          setApiBusy(false);
          return;
        }

        // Try to get the real ID and backend-assigned task number from the
        // upsert response body first.
        try {
          const upsertJson = await res.json();
          const newId = upsertJson?.id ?? upsertJson?.data?.id ?? upsertJson?.taskId;
          if (newId) resolvedId = String(newId);
          const newCode = upsertJson?.taskNumber ?? upsertJson?.data?.taskNumber;
          if (newCode) resolvedCode = newCode;
        } catch {}
        // Fallback: fetch the task list and match by id (or, if the id itself
        // never resolved above, take the most recently created task).
        if (resolvedId === '__new__' || !resolvedCode) {
          const listRes = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/tasks`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (listRes.ok) {
            const listJson = await listRes.json();
            const list: any[] = Array.isArray(listJson) ? listJson : (listJson?.data ?? []);
            const found = resolvedId !== '__new__'
              ? list.find((t: any) => String(t.id) === resolvedId)
              : list.reduce((max: any, t: any) => (!max || Number(t.id) > Number(max.id) ? t : max), null);
            if (found?.id) resolvedId = String(found.id);
            if (found?.taskNumber) resolvedCode = found.taskNumber;
          }
        }
      }

      saveSucceeded = true;
    } catch {
      setApiError('Failed to save. Please try again.');
      setApiBusy(false);
    } finally {
      setSaving(false);
    }
    if (!saveSucceeded) {
      setApiBusy(false);
      return;
    }
    const updated: CommandIssue = { ...issue, id: resolvedId, code: resolvedCode, title, description, status: effectiveStatus, assigneeType: effectiveAssigneeType, lastAssigneeType: issue.lastAssigneeType ?? (effectiveAssigneeType === 3 ? 2 : effectiveAssigneeType), categoryId: selectedCategoryId, comments: toSave,
      // Stamp creation time on first save so "Ticket Created" shows a date immediately.
      createdAt: issue.createdAt ?? (isNew ? new Date().toISOString() : undefined),
      linkedPolicyId, linkedPolicyNumber, linkedInsuredId, linkedInsuredName, linkedAgentId, linkedAgentName,
      agentName: effectiveAgentName || undefined,
      agentId: effectiveAgentName ? (effectiveAgentId || undefined) : undefined,
      files: uploadedFiles.length ? uploadedFiles.map((f) => ({ name: f.name, url: f.url })) : undefined,
      transactionId: transactionId ?? issue.transactionId,
    };
    // Send & Assign to AI: fire streaming AI in the background (no stream shown
    // here since the panel closes immediately after), close panel
    const handleAiError = (message: string) => {
      if (message) setApiError(message);
    };
    const shouldTriggerAi = !!triggerAi;
    // Explicit override (auto-run passes the AI-generated description without
    // posting it as a note) → first call (new ticket) → description;
    // subsequent calls → last human comment.
    const aiPromptText = (promptOverride ?? '').trim() || (isNew
      ? description
      : (toSave.filter(c => c.role?.toLowerCase() === 'human').slice(-1)[0]?.message ?? ''));

    if (closeAfter) {
      if (shouldTriggerAi) {
        void callMomentumKanbanStream(resolvedId, aiPromptText, handleAiError);
      }
      if (onSaveSilent) onSaveSilent(updated); else onSave(updated);
      setApiBusy(false);
      onClose();
      return;
    }
    // Streaming path: update parent, optionally fire non-streaming AI, then stream response inline
    if (onSaveSilent) {
      onSaveSilent(updated);
    } else {
      onSave(updated);
    }
    if (shouldTriggerAi) {
      callMomentumKanbanAI(resolvedId, handleAiError);
    }
    // The save itself is done — isThinking/streamingContent take over as the
    // busy signal for the AI turn that follows. Leaving apiBusy true past this
    // point makes "Upserting command..." reappear after the answer has already
    // arrived, once isThinking/thoughtText clear but before runInlineAgentStream's
    // trailing reconciliation fetch (re-fetching the task to backfill the real
    // comment id) finishes and resets it.
    setApiBusy(false);
    runInlineAgentStream(resolvedId, aiPromptText, updated);
  }, [issue, title, description, status, assigneeType, assigneeAgentName, assigneeAgentId, selectedCategoryId, comments, onSave, onSaveSilent, position, source,
      linkedPolicyId, linkedPolicyNumber, linkedInsuredId, linkedInsuredName, linkedAgentId, linkedAgentName, uploadedFiles, runInlineAgentStream, setStreamingContent]);

  const clearComposerAttachments = React.useCallback(() => {
    setCommentAttachedFiles([]);
    setPendingUploads([]);
    setShowAllUploadedFiles(false);
    setUploadError('');
  }, []);

  const buildCommentMessage = React.useCallback((text: string, files: Array<{ name: string; url?: string; size?: number }>): string => {
    const normalized = annotateComment(text.trim());
    if (!files.length) return normalized;
    const attachmentLines = files.map((f) => `- ${f.name}`);
    return `${normalized}\n\nAttached files:\n${attachmentLines.join('\n')}`;
  }, [annotateComment]);

  // Posts a reply to this command. Shared by the in-panel composer and the
  // global chat bar (when the bar is this command's composer).
  const sendReplyText = React.useCallback((raw: string) => {
    const text = raw.trim();
    const hasAttachments = commentAttachedFiles.length > 0;
    if (text || hasAttachments) {
      const message = buildCommentMessage(text || 'Attached files', commentAttachedFiles);
      const updated = [...comments, { role: 'Human', message, created: new Date().toISOString(), ...operatorIdentity() }];
      isNewCommentRef.current = true;
      setComments(updated);
      setNewComment('');
      clearInsertedNames();
      clearComposerAttachments();
      handleSave(updated);
    } else {
      handleSave();
    }
  }, [comments, handleSave, clearInsertedNames, commentAttachedFiles, clearComposerAttachments, buildCommentMessage]);
  const handleReply = React.useCallback(() => sendReplyText(newComment), [sendReplyText, newComment]);

  // One-click answer to an AI "Do you want me to proceed?" — posted exactly like
  // a typed reply (same save + agent run path), leaving any composer draft alone.
  const handleQuickReply = React.useCallback((text: string) => {
    const updated = [...comments, { role: 'Human', message: text, created: new Date().toISOString(), ...operatorIdentity() }];
    isNewCommentRef.current = true;
    setComments(updated);
    handleSave(updated);
  }, [comments, handleSave]);

  React.useEffect(() => {
    const prompt = (autoRunPrompt || '').trim();
    if (!prompt) return;
    if (mode !== 'edit') return;
    if (!issue.id || issue.id === '__new__') return;
    // Wait until the create flow is fully settled in the parent UI before
    // firing the first stream, otherwise fast streams can complete before the
    // panel visibly enters its normal state.
    if (externalBusy) return;
    if (saving || apiBusy) return;

    const key = `${issue.id}::${prompt}`;
    if (processedAutoRunKeys.has(key)) return;
    if (autoRunKeyRef.current === key) return;
    processedAutoRunKeys.add(key);
    autoRunKeyRef.current = key;

    // The create endpoint already seeded the thread's opening Human comment
    // (the AI-generated title) server-side. Appending the description here as
    // another note both duplicated the description field and diverged from the
    // server thread — which the 4s in-progress poll then stomped back, making
    // the description note visibly flicker away. Instead, keep the thread
    // as-is and hand the description to the stream as its instruction only.
    setStatus('inprogress');
    handleSave(comments, 'inprogress', assigneeType, false, false, undefined, undefined, prompt);
  }, [autoRunPrompt, mode, issue.id, comments, handleSave, saving, apiBusy, assigneeType, externalBusy]);

  // ── Re-attach to a run still executing server-side ──────────────────────────
  // The agent is driven by a task detached from the HTTP connection, so a page
  // refresh mid-execution never stops the run — but SSE has no replay, so the
  // panel used to come back with no sign of the work in flight and no thinking
  // indicator until the 4s task poll eventually surfaced the finished AI
  // comment. On mount (and whenever the open task changes) ask the backend
  // whether a run is live for this task and, if so, re-open its stream: the
  // recorded events we missed replay first, then it tails live to completion.
  //
  // The ref reset in the cleanup is deliberate: it lets React 18 StrictMode's
  // mount→unmount→mount cycle retry rather than have the first pass consume the
  // one attempt and the second find it already spent.
  const reattachedTaskRef = React.useRef<string>('');
  // Mirror of aiRunLive (computed further down each render) for the async
  // retry loop below — a closure over the state itself would go stale.
  const aiRunLiveRef = React.useRef(false);
  React.useEffect(() => {
    const taskId = issue.id;
    if (mode !== 'edit' || !taskId || taskId === '__new__') return;
    // A Done task has no live run to re-attach to — skip the active-run request.
    // Re-runs flip the status back, which re-runs this check.
    if (issue.status === 'done') return;
    if (reattachedTaskRef.current === taskId) return;
    reattachedTaskRef.current = taskId;

    // A stream this panel started itself is already rendering — nothing to
    // re-attach to, and re-attaching would double up on the same events.
    const localStreamLive = () => !!streamAbortRef.current && !streamAbortRef.current.signal.aborted;
    if (localStreamLive()) return;

    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      // A run triggered outside this panel (email/voice/webhook → the
      // fire-and-forget endpoint) may not have written its first event-log
      // line yet when the panel mounts — a one-shot check then misses it and
      // "Connecting to agent..." sits until the 4s poll delivers the finished
      // comment. While the thread still awaits the AI's reply, retry the
      // active check so the stream attaches as soon as the run's log appears.
      let run = await getActiveKanbanRun(taskId);
      let attempts = 0;
      while (!cancelled && !run.active && aiRunLiveRef.current && attempts < 20) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        if (cancelled || localStreamLive()) return;
        run = await getActiveKanbanRun(taskId);
        attempts += 1;
      }
      if (cancelled || !run.active || localStreamLive()) return;
      // Rejects on abort (panel closed before the stream opened) — a null body
      // is the same outcome as "nothing to re-attach to".
      const body = await openKanbanReattachStream(taskId, ctrl.signal).catch(() => null);
      if (cancelled || !body || localStreamLive()) return;

      streamAbortRef.current = ctrl;
      setThoughtText('');
      setCurrentTool('');
      setStreamingContent('');
      setStreamText('');
      setAiRunError(null);
      setIsThinking(true);
      let accAnswer = '';
      // Same turn-boundary reset as the POST path: a thought/tool event after
      // answer text marks a new agent turn whose answer restates everything —
      // start the accumulator over instead of gluing turns together.
      let answerTurnStale = false;
      const runError: { current: KanbanRunError | null } = { current: null };
      try {
        await consumeKanbanSse(body, ctrl.signal, (parsed) => {
          const isDone = parsed.responseStreaming === false || parsed.ResponseLoading === false;
          if (parsed.type === 'thought') {
            if ((parsed.thought || parsed.currentTool) && accAnswer) answerTurnStale = true;
            if (parsed.thought) setThoughtText(prev => prev + parsed.thought);
            if (parsed.currentTool) setCurrentTool(parsed.currentTool);
          } else {
            const runErr = kanbanRunError(parsed);
            if (runErr) runError.current = runErr;
            const chunk = parsed.type === 'answer'
              ? (parsed.response ?? '')
              : (parsed.Answer ?? parsed.response ?? parsed.content ?? parsed.text ?? '');
            if (chunk) {
              if (answerTurnStale) {
                accAnswer = '';
                answerTurnStale = false;
              }
              accAnswer = foldAnswerChunk(accAnswer, chunk);
              queueStreamingContent(accAnswer);
            }
          }
          if (isDone) {
            // Backend go-ahead decision rides on the terminal answer event.
            if (parsed.type === 'answer') setAiProceedPrompt(proceedPromptFromEvent(parsed));
            return 'stop';
          }
        }, false);
      } catch {
        // Aborted (panel closed / task switched) or the connection dropped —
        // either way the run itself is unaffected and the finally below settles
        // the UI. A dropped connection can be picked up by re-opening the task.
      } finally {
        if (!ctrl.signal.aborted) {
          setIsThinking(false);
          setThoughtText('');
          setCurrentTool('');
          setStreamingContent('');
          // Show the answer transiently, exactly as the POST path does. The
          // persisted AI comment arrives through the parent's 4s task-detail
          // poll (active while the task is In Progress) and replaces it.
          setStreamText(accAnswer);
          if (runError.current) setAiRunError(runError.current);
        }
        if (streamAbortRef.current === ctrl) streamAbortRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      if (reattachedTaskRef.current === taskId) reattachedTaskRef.current = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, issue.id, issue.status === 'done']);

  // Core upload shared by the local file picker and the AMS drive selector.
  // Throws on failure so each caller can surface the error in its own UI.
  const uploadSingleFile = React.useCallback(async (file: File) => {
    if (!transactionIdRef.current) {
      transactionIdRef.current = createKanbanTransactionId();
    }
    const res = await uploadMomentumKanbanFile(file, transactionIdRef.current);
    const fileUrl = typeof res?.fileUrl === 'string' ? res.fileUrl : undefined;
    const fileName = (typeof res?.fileName === 'string' && res.fileName) ? res.fileName : file.name;
    // NOTE: res.transactionId is the task-level transaction id (shared by every
    // file uploaded for this task) — it is NOT unique per file. Used on its own
    // as a React list key, it collides across files and breaks reconciliation
    // (symptom: the most-recently-uploaded file appears to duplicate over an
    // earlier one on re-render). Suffix it with a per-upload stamp so each
    // entry's key is unique regardless of how many files share the same
    // underlying transaction id.
    const uploadedTxId = `${(typeof res?.transactionId === 'string' && res.transactionId) || transactionIdRef.current || 'tx'}-${Date.now()}-${fileName}`;
    // Replace rather than duplicate if this filename is already tracked
    // (re-upload of the same name, or a double-fired upload event).
    setUploadedFiles((prev) => {
      const key = fileName.toLowerCase();
      const withoutDupe = prev.filter((f) => f.name.toLowerCase() !== key);
      return [{ name: fileName, url: fileUrl, size: file.size, transactionId: uploadedTxId }, ...withoutDupe];
    });
    setCommentAttachedFiles((prev) => {
      const key = fileName.toLowerCase();
      const withoutDupe = prev.filter((f) => f.name.toLowerCase() !== key);
      return [{ name: fileName, url: fileUrl, size: file.size, transactionId: uploadedTxId }, ...withoutDupe];
    });
  }, []);

  // Uploads start as soon as files are dropped/pasted/chosen — no extra click.
  const handleUploadFiles = React.useCallback(async (files: File[]) => {
    if (uploadingFile || files.length === 0) return;
    const validFiles = files.filter((f) => !!f && f.size > 0);
    if (!validFiles.length) return;
    if (uploadingFile) return;
    setUploadError('');
    const pendingBatch = validFiles.map((f, idx) => ({ id: `${Date.now()}-${idx}-${f.name}`, name: f.name, size: f.size }));
    setPendingUploads((prev) => [...prev, ...pendingBatch]);
    setUploadingFile(true);
    try {
      for (let i = 0; i < validFiles.length; i += 1) {
        const file = validFiles[i];
        const pendingId = pendingBatch[i]?.id;
        try {
          await uploadSingleFile(file);
        } finally {
          if (pendingId) setPendingUploads((prev) => prev.filter((p) => p.id !== pendingId));
        }
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to upload file');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadingFile(false);
    }
  }, [uploadingFile, uploadSingleFile]);

  const handleUploadFile = React.useCallback(async (file: File) => {
    await handleUploadFiles([file]);
  }, [handleUploadFiles]);

  const handleFileDragEnter = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!hasDragItems(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setFileDragOver(true);
  }, [hasDragItems]);

  const handleFileDragOver = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!hasDragItems(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setFileDragOver(true);
  }, [hasDragItems]);

  const handleFileDragLeave = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!fileDragOver) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setFileDragOver(false);
  }, [fileDragOver]);

  const handleFileDrop = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    const files = readFilesFromDataTransfer(e.dataTransfer);
    if (!files.length) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setFileDragOver(false);
    setAttachMenuOpen(false);
    void handleUploadFiles(files);
  }, [handleUploadFiles, readFilesFromDataTransfer]);

  // AMS-drive selections are uploaded server-side: the kanban API downloads the
  // files from AMS and stores them in this task's transaction (the AMS
  // pre-signed download URLs are not CORS-enabled, so the browser cannot fetch
  // the bytes itself). Errors propagate so the AMS dialog stays open and shows
  // them.
  const handleAmsFilesUpload = React.useCallback(async (selections: AmsUploadSelection[]) => {
    if (!selections.length) return;
    setUploadError('');
    setUploadingFile(true);
    try {
      if (!transactionIdRef.current) {
        transactionIdRef.current = createKanbanTransactionId();
      }
      let uploadedCount = 0;
      for (const selection of selections) {
        const { files } = await uploadMomentumKanbanAmsFiles(selection, transactionIdRef.current);
        for (const uploaded of files) {
          const fileName = uploaded.fileName;
          if (!fileName) continue;
          uploadedCount += 1;
          // Same per-upload key suffix as uploadSingleFile — the transaction id
          // is shared by every file on the task, so it can't be the key alone.
          const uploadedTxId = `${transactionIdRef.current || 'tx'}-${Date.now()}-${fileName}`;
          setUploadedFiles((prev) => {
            const key = fileName.toLowerCase();
            const withoutDupe = prev.filter((f) => f.name.toLowerCase() !== key);
            return [{ name: fileName, url: uploaded.fileUrl, transactionId: uploadedTxId }, ...withoutDupe];
          });
          setCommentAttachedFiles((prev) => {
            const key = fileName.toLowerCase();
            const withoutDupe = prev.filter((f) => f.name.toLowerCase() !== key);
            return [{ name: fileName, url: uploaded.fileUrl, transactionId: uploadedTxId }, ...withoutDupe];
          });
        }
      }
      if (uploadedCount === 0) {
        throw new Error('No files could be uploaded from the AMS drive.');
      }
    } finally {
      setUploadingFile(false);
    }
  }, []);

  const handleOpenFile = React.useCallback(async (fileName: string, fallbackUrl?: string) => {
    const txId = transactionIdRef.current || issue.transactionId;
    if (!txId) {
      if (fallbackUrl) {
        setPreviewFile({ name: fileName, url: fallbackUrl });
        return;
      }
      setUploadError('Missing transaction id for this task.');
      return;
    }
    setUploadError('');
    setResolvingFileName(fileName);
    try {
      const url = await getKanbanFileUrl(txId, fileName);
      if (url) {
        setPreviewFile({ name: fileName, url });
      } else if (fallbackUrl) {
        setPreviewFile({ name: fileName, url: fallbackUrl });
      } else {
        setUploadError('File URL not found.');
      }
    } catch (err: any) {
      if (fallbackUrl) {
        setPreviewFile({ name: fileName, url: fallbackUrl });
      } else {
        setUploadError(err?.message || 'Failed to open file.');
      }
    } finally {
      setResolvingFileName(null);
    }
  }, [issue.transactionId]);

  const handleViewDetailsForComment = React.useCallback(async (comment: CommentModel, index: number) => {
    const toText = (v: any): string => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v, null, 2));
    const parseJsonSafe = (value: any): any => {
      if (typeof value !== 'string') return value;
      try { return JSON.parse(value); } catch { return value; }
    };
    const tryExtractEmbeddedJson = (value: string): any => {
      if (!value) return null;
      const direct = parseJsonSafe(value);
      if (direct && typeof direct === 'object') return direct;
      const start = value.indexOf('{');
      const end = value.lastIndexOf('}');
      if (start >= 0 && end > start) {
        const candidate = parseJsonSafe(value.slice(start, end + 1));
        if (candidate && typeof candidate === 'object') return candidate;
      }
      return null;
    };
    const flattenToolExecutions = (value: any): any[] => {
      const parsedValue = parseJsonSafe(value);
      if (!parsedValue) return [];
      if (Array.isArray(parsedValue)) {
        return parsedValue.flatMap((item: any) => {
          const parsedItem = parseJsonSafe(item);
          if (Array.isArray(parsedItem?.tool_executions)) return parsedItem.tool_executions;
          if (Array.isArray(parsedItem?.toolHistory)) return parsedItem.toolHistory;
          return [parsedItem];
        });
      }
      if (Array.isArray(parsedValue?.tool_executions)) return parsedValue.tool_executions;
      if (Array.isArray(parsedValue?.toolHistory)) return parsedValue.toolHistory;
      return [parsedValue];
    };
    const mapExecs = (entries: any[]) => entries
      .map((e: any) => ({
        tool: String(e?.tool ?? e?.toolName ?? e?.name ?? e?.tool_name ?? 'tool'),
        action: toText(e?.action ?? e?.input ?? e?.request ?? e?.tool_input),
        result: toText(e?.result ?? e?.output ?? e?.response ?? e?.tool_output),
        status: String(e?.status ?? e?.state ?? e?.execution_status ?? 'success'),
      }))
      .filter((e: any) => e.tool || e.action || e.result);

    const runId = `${issue.id || 'preview-task'}:comment:${comment.id || index}`;
    const startedAt = comment.created || issue.createdAt || issue.updatedAt || new Date().toISOString();

    // If policy/insured side-detail is open, close it so the comment's run details can be shown.
    setSelectedInsuredId(null);
    setSelectedPolicyId(null);

    setRunDetailPanel({
      run: {
        id: runId,
        status: toRunDetailStatus(issue.status),
        startedAt,
        finishedAt: comment.created || issue.updatedAt || undefined,
        notes: 'Comment-level execution details',
      },
      execs: null,
      exeLoading: true,
      kind: 'command',
    });

    const embedded = tryExtractEmbeddedJson(String(comment.message ?? ''));
    const localRaw = (comment as any)?.toolHistory ?? embedded?.tool_history ?? embedded?.toolHistory ?? embedded?.tool_executions;
    let resolvedExecs = mapExecs(flattenToolExecutions(localRaw));

    if (issue.id && issue.id !== '__new__') {
      try {
        // Shared with the page's detail load of this task when both start together.
        const json = await fetchKanbanTaskJson(issue.id);
        const detail = (json?.data ?? json ?? {}) as any;
        const rawComments = detail?.commentsData ?? parseJsonSafe(detail?.comments) ?? [];
        const commentsList = Array.isArray(rawComments) ? rawComments : [];

        const backendComment = commentsList.find((c: any) => {
          const cId = String(c?.id ?? c?.Id ?? '');
          const targetId = String(comment.id ?? '');
          if (targetId && cId) return cId === targetId;
          const msg = String(c?.message ?? c?.Message ?? '').trim();
          return !!msg && msg === String(comment.message ?? '').trim();
        });

        const backendRaw = backendComment
          ? (backendComment?.toolHistory ?? backendComment?.ToolHistory)
          : (detail?.toolHistoryData ?? detail?.toolHistory ?? detail?.tool_history);
        const backendExecs = mapExecs(flattenToolExecutions(backendRaw));
        if (backendExecs.length > 0) resolvedExecs = backendExecs;
      } catch {
        // keep local parsed executions when fetch fails
      }
    }

    const finalExecs = resolvedExecs;
    const runStatus = finalExecs.some((e) => String(e.status).toLowerCase() === 'failed') ? 'failed' : 'done';

    setRunDetailPanel(prev => {
      if (!prev || prev.run.id !== runId) return prev;
      return {
        run: { ...prev.run, status: runStatus },
        execs: finalExecs,
        exeLoading: false,
      };
    });
  }, [issue.id, issue.status, issue.createdAt, issue.updatedAt, setRunDetailPanel, setSelectedInsuredId, setSelectedPolicyId]);

  // Auto-open the last chat message's tool-execution details in the right sidebar as
  // soon as this command's panel opens (and again whenever a new comment lands) —
  // the same view "View Details" opens manually, just surfaced without an extra click.
  // However, don't auto-open if a different command's detail panel is already displayed.
  //
  // Deliberately NOT keying this effect on runDetailPanel?.run.id — closing the panel
  // (setRunDetailPanel(null)) changes that value too, which would re-run this effect
  // and instantly reopen the very panel the user just closed. It only needs to react
  // to an actually new comment (or switching to a different task); the "is a different
  // command's panel showing" check below just reads the latest value at that moment.
  const lastComment = comments[comments.length - 1];
  // Keyed on `created` first: the list preview's last comment has no id while the
  // full-detail copy of the same comment does, so keying on id made this effect
  // treat the detail merge as a "new" comment and refetch run details.
  const lastCommentKey = lastComment ? (lastComment.created || lastComment.id || lastComment.message) : null;
  React.useEffect(() => {
    if (!lastComment) return;
    // Extract the command ID from the currently displayed run panel
    const currentRunId = runDetailPanel?.run.id;
    const currentCommandId = currentRunId?.split(':')[0]; // runId format: "${issue.id}:comment:..."
    // Only auto-open if no other command's panel is displayed, or if it's already this command's panel
    if (currentRunId && currentCommandId !== issue.id) {
      // A different command's detail panel is currently displayed, so don't replace it
      return;
    }
    handleViewDetailsForComment(lastComment, comments.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.id, lastCommentKey]);

  // Clear the right-panel run details when this task's panel closes, so the sidebar
  // reverts to the Command Center overview instead of showing a stale run.
  React.useEffect(() => () => setRunDetailPanel(null), [setRunDetailPanel]);

  const renderHighlightedText = React.useCallback((text: string): React.ReactNode => {
    if (!insertedNames.length) return text;
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;
    while (remaining.length > 0) {
      const matched = insertedNames.find(n => remaining.startsWith(n));
      if (matched) {
        parts.push(
          <mark key={key++} style={{ background: 'rgba(99,102,241,0.28)', color: 'transparent', borderRadius: 3, outline: '1px solid rgba(99,102,241,0.4)' }}>
            {matched}
          </mark>
        );
        remaining = remaining.slice(matched.length);
      } else {
        let i = 1;
        while (i < remaining.length && !insertedNames.some(n => remaining.slice(i).startsWith(n))) i++;
        parts.push(<React.Fragment key={key++}>{remaining.slice(0, i)}</React.Fragment>);
        remaining = remaining.slice(i);
      }
    }
    return parts;
  }, [insertedNames]);

  // Renders a saved comment string — converts $$IDF/PDF$$ tokens to clickable chips
  const renderCommentContent = React.useCallback((rawText: string): React.ReactNode => {
    const parsed = parseCommentDisplay(rawText);
    const text = processEntityLinks(
      parsed.body
        // Backward compatibility: older non-attachment lines that were stored as
        // markdown links should still render without exposing raw URL text.
        .replace(/^-\s*\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)(.*)$/gm, '- $1$3')
        .replace(/<!--MOMENTUM_IDS:[^>]*-->/g, '')
        .replace(/^Task Status:.*$/gm, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    );
    return (
      <>
        {!!text && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={linkComponents}>{text}</ReactMarkdown>
        )}
        {parsed.attachments.length > 0 && (
          <div className="mc-comment-files" aria-label="Attached files">
            {parsed.attachments.map((name, idx) => (
              <button
                key={`${name}-${idx}`}
                type="button"
                className="mc-comment-file-chip"
                title={`Preview ${name}`}
                onClick={(e) => { e.stopPropagation(); void handleOpenFile(name); }}
                disabled={resolvingFileName === name}
              >
                <span className="mc-comment-file-chip__icon">
                  {resolvingFileName === name ? <FiRefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <FiPaperclip size={11} />}
                </span>
                <span className="mc-comment-file-chip__name">{name}</span>
              </button>
            ))}
          </div>
        )}
      </>
    );
  }, [linkComponents, handleOpenFile, resolvingFileName]);

  // The notes feed re-renders on every streamed chunk and every composer
  // keystroke; reuse each comment's rendered markdown until its text or the
  // renderer itself changes, so a long thread isn't re-parsed per token.
  // renderCommentContent is the cache's invalidation key, not an input to it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderedCommentCache = React.useMemo(() => new Map<string, React.ReactNode>(), [renderCommentContent]);
  const renderCommentContentCached = React.useCallback((rawText: string): React.ReactNode => {
    let node = renderedCommentCache.get(rawText);
    if (node === undefined) {
      node = renderCommentContent(rawText);
      renderedCommentCache.set(rawText, node);
    }
    return node;
  }, [renderedCommentCache, renderCommentContent]);

  const getDraftValues = React.useCallback((key: string, original: ParsedEmailDraft) => {
    return draftEdits[key] || original;
  }, [draftEdits]);

  const updateDraftField = React.useCallback((key: string, original: ParsedEmailDraft, field: keyof ParsedEmailDraft, value: string) => {
    setDraftEdits(prev => ({
      ...prev,
      [key]: { ...(prev[key] || original), [field]: value },
    }));
    setDraftErrors(prev => { if (!prev[key]) return prev; const next = { ...prev }; delete next[key]; return next; });
  }, []);

  const handleSendDraft = React.useCallback(async (commentIndex: number, draftIndex: number, original: ParsedEmailDraft) => {
    const key = `${commentIndex}-${draftIndex}`;
    if (sentDrafts[key] || sendingDraft === key) return;
    const draft = draftEdits[key] || original;
    const invalid = validateDraftForSend(draft);
    if (invalid) {
      setDraftErrors(prev => ({ ...prev, [key]: invalid }));
      return;
    }
    setDraftErrors(prev => { if (!prev[key]) return prev; const next = { ...prev }; delete next[key]; return next; });
    setSendingDraft(key);
    try {
      await sendKanbanEmail({
        to: draft.to,
        subject: draft.subject,
        body: draft.body,
        cc: draft.cc || undefined,
        taskId: issue.id,
        taskNumber: issue.code,
      });
      setSentDrafts(prev => ({ ...prev, [key]: true }));
    } catch (err: any) {
      const reason = (err?.message || '').trim();
      setDraftErrors(prev => ({ ...prev, [key]: `Couldn't send the email${reason ? `: ${reason}` : '.'} Your draft is unchanged; try again.` }));
    } finally {
      setSendingDraft(null);
    }
  }, [sentDrafts, sendingDraft, issue.id, issue.code, draftEdits]);

  // Send Email → validate first (inline error, no dialog), then confirm.
  const requestSendDraft = React.useCallback((commentIndex: number, draftIndex: number, original: ParsedEmailDraft) => {
    const key = `${commentIndex}-${draftIndex}`;
    if (sentDrafts[key] || sendingDraft === key) return;
    const invalid = validateDraftForSend(draftEdits[key] || original);
    if (invalid) {
      setDraftErrors(prev => ({ ...prev, [key]: invalid }));
      return;
    }
    setDraftErrors(prev => { if (!prev[key]) return prev; const next = { ...prev }; delete next[key]; return next; });
    setConfirmDraft({ key, commentIndex, draftIndex, original });
  }, [sentDrafts, sendingDraft, draftEdits]);

  // Per-response feedback — each AI comment carries its own like/dislike/feedback,
  // saved via the dedicated comment-feedback endpoint (matched server-side by comment id).
  // Comments without an id (legacy rows, or an optimistic bubble mid-stream) can't be
  // targeted yet, so the buttons stay hidden until a real id arrives on refetch.
  const handleCommentFeedback = React.useCallback((index: number, patch: { isLiked?: boolean; isDisliked?: boolean; feedback?: string }) => {
    const target = comments[index];
    if (!target?.id) return;
    const next: CommentModel = {
      ...target,
      isLiked: patch.isLiked ?? target.isLiked ?? false,
      isDisliked: patch.isDisliked ?? target.isDisliked ?? false,
      feedback: patch.feedback ?? target.feedback ?? '',
    };
    setComments(prev => prev.map((c, i) => i === index ? next : c));

    const authToken = localStorage.getItem('authToken') || '';
    fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/comment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        taskId: Number(issue.id),
        commentId: target.id,
        isLiked: next.isLiked,
        isDisliked: next.isDisliked,
        feedback: next.feedback,
      }),
    }).catch(() => {});
  }, [comments, issue.id]);

  const handleToggleCommentLike = React.useCallback((index: number) => {
    const c = comments[index];
    if (!c) return;
    handleCommentFeedback(index, { isLiked: !c.isLiked, isDisliked: false });
  }, [comments, handleCommentFeedback]);

  const handleToggleCommentDislike = React.useCallback((index: number) => {
    const c = comments[index];
    if (!c) return;
    handleCommentFeedback(index, { isDisliked: !c.isDisliked, isLiked: false });
  }, [comments, handleCommentFeedback]);

  const handleOpenCommentFeedbackEditor = React.useCallback((index: number, e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const popupWidth = 360;
    const popupHeight = 140;
    const padding = 16;
    let top = rect.bottom + 6;
    let left = rect.left;
    if (left + popupWidth > window.innerWidth) left = window.innerWidth - popupWidth - padding;
    if (left < padding) left = padding;
    if (top + popupHeight > window.innerHeight) top = rect.top - popupHeight - 6;
    if (top < padding) top = padding;

    setFeedbackDraft(comments[index]?.feedback || '');
    setFeedbackEditorPos({ top, left });
    setFeedbackEditorIndex(index);
  }, [comments]);

  const closeCommentFeedbackEditor = React.useCallback(() => {
    setFeedbackEditorIndex(null);
    setFeedbackEditorPos(null);
  }, []);

  const handleSaveCommentFeedback = React.useCallback(() => {
    if (feedbackEditorIndex === null) return;
    handleCommentFeedback(feedbackEditorIndex, { feedback: feedbackDraft });
    closeCommentFeedbackEditor();
  }, [feedbackEditorIndex, feedbackDraft, handleCommentFeedback, closeCommentFeedbackEditor]);

  const currentCol = COMMAND_COLUMNS.find(c => c.key === status)!;

  const border = 'var(--border-glass, rgba(255,255,255,0.09))';
  const inputBg = (onBack && theme === 'dark') ? 'var(--surface-card)' : 'transparent';
  const selectBg = theme === 'dark' ? 'var(--surface-card, #1e1e35)' : '#fff';
  const optionStyle: React.CSSProperties = theme === 'dark'
    ? { background: '#1e1e35', color: '#e2e8f0' }
    : { background: '#ffffff', color: '#111827' };
  // Solid background for floating popovers (format menu, send dropdown) —
  // var(--surface-card) is a near-transparent tint (rgba(255,255,255,0.042)
  // in dark mode) meant for subtle card overlays, not an opaque popup
  // surface, so using it here let the comment feed show through underneath.
  const popoverBg = theme === 'dark' ? '#1e1e35' : '#ffffff';
  const toolbarBtn: React.CSSProperties = {
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'none', border: `1px solid ${border}`, borderRadius: 5,
    fontSize: 13, fontFamily: FONT, color: 'var(--text-secondary)', cursor: 'default', flexShrink: 0,
  };

  const normalizedAssigneeAgentName = assigneeAgentName.trim();
  const matchedDwAgent = normalizedAssigneeAgentName
    ? dwAgents.find(a => a.name.trim().toLowerCase() === normalizedAssigneeAgentName.toLowerCase())
    : undefined;
  // Only a type-2 (Digital Workforce) assignee can be an "unmatched agent name"
  // fallback — otherwise a shared human agent's name (assigneeType 0, matched
  // below against sharedAgents instead) would spuriously trip this too.
  const hasUnmatchedAgentName = assigneeType === 2 && !!normalizedAssigneeAgentName && !matchedDwAgent;
  const matchedSharedAgent = assigneeType === 0 && normalizedAssigneeAgentName
    ? (sharedAgents.find(a => a.id === assigneeAgentId) || sharedAgents.find(a => a.name.trim().toLowerCase() === normalizedAssigneeAgentName.toLowerCase()))
    : undefined;
  // A task still shows "inprogress" + the header's Live tag after a remount (e.g.
  // navigating away mid-run and back) even though this fresh mount never set
  // isThinking/streamingContent itself — fold the parent's poll signal into busy
  // state so the composer disables and the skeleton reappears, matching the Live tag.
  // The poll signal alone only means "task sits in the In-progress column" — an
  // agent that finishes but leaves the task there (e.g. its final status update
  // keeps it In progress for human follow-up) would pin "Connecting to agent..."
  // forever, so treat the run as live only while the thread still awaits the
  // AI's reply.
  const lastCommentRole = (comments[comments.length - 1]?.role ?? '').trim().toLowerCase();
  const awaitingAiReply = !(lastCommentRole.includes('ai') || lastCommentRole === 'assistant');
  const aiRunLive = isPolling && assigneeType === 2 && awaitingAiReply;
  aiRunLiveRef.current = aiRunLive;
  const sendBusy = saving || apiBusy || externalBusy || aiRunLive;
  const busyLabel = React.useMemo(() => {
    if (isThinking) return 'AI is working...';
    if (externalBusy) return 'Creating task…';
    if (saving || apiBusy) return 'Saving task…';
    // Poll-driven busy state only — the run was started outside this mount
    // (email/voice trigger or a pre-remount send) and no live stream is
    // attached yet. Once the re-attach stream opens, isThinking takes over
    // and the label flips to "AI is working...".
    if (aiRunLive) return 'Connecting to agent...';
    return '';
  }, [isThinking, externalBusy, saving, apiBusy, aiRunLive]);
  const showBusyIndicator = !!busyLabel && !streamingContent && !thoughtText;
  // Show skeleton when busy AND not thinking (thinking block replaces skeleton).
  // Keep showing skeleton even while content streams to indicate live updates are coming.
  const showBusySkeleton = (sendBusy || isThinking) && !thoughtText;
  // Covers every case the thinking block itself can be visible, including a
  // stream that ended without a final answer (isThinking flips false but
  // thoughtText is left on screen) — Send must never look idle while any of
  // this is showing. uploadingFile is included so a message can't be sent
  // while its attachment hasn't finished uploading yet.
  const isComposerBusy = sendBusy || !!streamingContent || isThinking || !!thoughtText || uploadingFile;

  // Screen-reader announcements for an AI run: one message when it starts and
  // one when it ends. The streamed text itself is deliberately not live.
  const aiResponding = isThinking || !!streamingContent || aiRunLive;
  const [aiLiveMessage, setAiLiveMessage] = React.useState('');
  const aiRespondingPrevRef = React.useRef(false);
  React.useEffect(() => {
    if (aiResponding === aiRespondingPrevRef.current) return;
    aiRespondingPrevRef.current = aiResponding;
    setAiLiveMessage(aiResponding ? 'AI is responding…' : (aiRunError ? '' : 'AI response ready'));
  }, [aiResponding, aiRunError]);

  // Send options menu: move focus into it on open (menu keyboard pattern).
  React.useEffect(() => {
    if (!sendDropdownOpen) return;
    const first = sendMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])');
    first?.focus();
  }, [sendDropdownOpen]);

  // ── The app-wide chat bar is this command's composer ──
  // An existing command claims the docked bar: it keeps its look, but what
  // you type there posts to this command (same reply path as the in-panel
  // composer — agent runs, attachments and all), and files dropped or picked
  // there upload as this task's files. The in-panel input row is hidden only
  // while the bar is actually on screen (dockReady), so there's always a way
  // to reply. New, unsaved commands keep the in-panel composer.
  const { setChatTarget, dockReady } = useActiveAgent();
  useAgentPhotosVersion();
  const claimsBar = mode === 'edit' && !!issue.id && issue.id !== '__new__';
  const sendReplyRef = React.useRef(sendReplyText);
  sendReplyRef.current = sendReplyText;
  const uploadRef = React.useRef(handleUploadFiles);
  uploadRef.current = handleUploadFiles;
  // This command's attachments, for the bar's file tray.
  const barFiles = React.useMemo(() => [
    ...commentAttachedFiles.map((f) => ({ key: `file:${f.name}`, name: f.name, size: f.size, status: 'ready' as const })),
    ...pendingUploads.map((p) => ({ key: `pending:${p.id}`, name: p.name, size: p.size, status: 'uploading' as const })),
    ...(uploadError ? [{ key: 'error', name: 'Upload', status: 'error' as const, error: uploadError }] : []),
  ], [commentAttachedFiles, pendingUploads, uploadError]);
  React.useEffect(() => {
    if (!claimsBar) return;
    setChatTarget({
      label: issue.code ? `#${issue.code}` : 'this task',
      // @-links typed in the bar are saved in this panel's own format, so
      // they render as the same clickable insured/policy links.
      submit: (text, mentions) => sendReplyRef.current(annotateWith(text, mentions)),
      openAmsPicker: () => setShowAmsSelector(true),
      uploadFiles: (files) => { void uploadRef.current(files); },
      busy: isComposerBusy,
      hasAttachments: commentAttachedFiles.length > 0,
      files: barFiles,
      removeFile: (key) => {
        if (key === 'error') { setUploadError(''); return; }
        if (key.startsWith('file:')) {
          const name = key.slice(5);
          setCommentAttachedFiles((prev) => prev.filter((f) => f.name !== name));
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsBar, issue.code, isComposerBusy, commentAttachedFiles.length, barFiles, setChatTarget]);
  React.useEffect(() => {
    if (!claimsBar) return;
    return () => setChatTarget(null);
  }, [claimsBar, setChatTarget]);
  const barIsComposer = claimsBar && dockReady;

  // The command's agent shows as replying in the Digital Team strip while it
  // works on your message here.
  useAgentReplySignal(assigneeAgentId || issue.agentId, isThinking || !!streamingContent);
  // Yes / No quick replies only ever attach to the newest visible comment: an
  // older "proceed?" has already been answered by whatever followed it.
  let lastVisibleCommentIndex = -1;
  for (let ci = comments.length - 1; ci >= 0; ci--) {
    if ((comments[ci].message ?? '').trim()) { lastVisibleCommentIndex = ci; break; }
  }
  const [statusSaving, setStatusSaving] = React.useState(false);
  const [assigneeSaving, setAssigneeSaving] = React.useState(false);
  const assigneeSelectValue = assigneeType === 2 && normalizedAssigneeAgentName
    ? (matchedDwAgent ? `dw:${matchedDwAgent.id}` : `dw_name:${normalizedAssigneeAgentName}`)
    : matchedSharedAgent
      ? `agent:${matchedSharedAgent.id}`
      : String(assigneeType);

  const resolveAssigneeSelection = React.useCallback((rawValue: string) => {
    if (rawValue.startsWith('dw:')) {
      const agentId = rawValue.slice(3);
      const agent = dwAgents.find(a => a.id === agentId);
      return {
        nextAssigneeType: 2,
        nextAgentName: agent?.name || '',
        nextAgentId: agentId,
      };
    }
    if (rawValue.startsWith('agent:')) {
      const agentId = rawValue.slice('agent:'.length);
      const agent = sharedAgents.find(a => a.id === agentId);
      return {
        nextAssigneeType: 0,
        nextAgentName: agent?.name || '',
        nextAgentId: agentId,
      };
    }
    if (rawValue.startsWith('dw_name:')) {
      return {
        nextAssigneeType: 2,
        nextAgentName: rawValue.slice('dw_name:'.length),
        nextAgentId: '',
      };
    }
    // Picking the plain "User" option while a specific shared agent is selected
    // is the explicit way back to the generic bucket — clear the named agent
    // instead of leaving it in place (which would just re-select the same
    // shared-agent option on the next render).
    if (rawValue === '0' && matchedSharedAgent) {
      return { nextAssigneeType: 0, nextAgentName: '', nextAgentId: '' };
    }
    // Switching to "Me" or generic "AI" keeps whatever agent was already on the
    // task instead of blanking it — the last delegated agent stays visible/
    // restorable if the user switches back to a specific Digital Workforce agent.
    return {
      nextAssigneeType: Number(rawValue),
      nextAgentName: assigneeAgentName,
      nextAgentId: assigneeAgentId,
    };
  }, [dwAgents, sharedAgents, assigneeAgentName, assigneeAgentId, matchedSharedAgent]);

  const handleStatusChange = React.useCallback(async (nextStatus: CommandStatus) => {
    if (nextStatus === status) return;
    const prevStatus = status;
    setStatus(nextStatus);
    setApiError('');

    // Draft task: keep local state only; persistence occurs on first Save/Send.
    if (!issue.id || issue.id === '__new__') return;

    const authToken = localStorage.getItem('authToken') || '';
    const transactionId = transactionIdRef.current || issue.transactionId || undefined;
    // Local view of the task's agent attribution, for the optimistic update and
    // the run kick-off below. Deliberately NOT sent to the server: the status
    // upsert omits the agent fields so the stored values survive untouched.
    const currentAgentName = assigneeAgentName.trim() || normalizeAgentName(issue.agentName);
    const currentAgentId = assigneeAgentId || issue.agentId || '';

    setStatusSaving(true);
    try {
      const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/task/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          ...(transactionId ? { 'X-Transaction-Id': transactionId } : {}),
        },
        body: JSON.stringify({
          id: Number(issue.id),
          taskNumber: issue.code || null,
          statusId: KANBAN_STATUS_IDS[nextStatus],
          title,
          description,
          comments: toCommentsJson(comments),
          assigneeId: issue.assigneeId ?? 0,
          // A status change is not an assignee change. assigneeType,
          // lastAssigneeType, agentName and agentId are deliberately omitted so
          // upsert_kanban_task keeps whatever is stored (see
          // kanban_upsert_assignee_guard_migration.sql).
          //
          // Replaying them here is what broke tasks: this panel's copy of
          // assigneeType could still say "agent" after the agent had already
          // finished and handed the task back, so moving the card re-claimed it
          // for the agent and it vanished from "Assigned to me". The agentName
          // fallback was worse -- it wrote the literal string 'User' over the
          // real agent name, permanently losing which agent did the work.
          position,
          source,
          categoryId: selectedCategoryId,
          files: uploadedFiles.length
            ? JSON.stringify(uploadedFiles.map((f) => f.name))
            : null,
          transactionId,
        }),
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const b = await res.json();
          msg = b.errorMessage || b.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const updated: CommandIssue = {
        ...issue,
        title,
        description,
        status: nextStatus,
        assigneeType,
        lastAssigneeType: issue.lastAssigneeType ?? (assigneeType === 3 ? 2 : assigneeType),
        categoryId: selectedCategoryId,
        comments,
        linkedPolicyId,
        linkedPolicyNumber,
        linkedInsuredId,
        linkedInsuredName,
        linkedAgentId,
        linkedAgentName,
        agentName: currentAgentName || undefined,
        agentId: currentAgentName ? (currentAgentId || undefined) : undefined,
        files: uploadedFiles.length ? uploadedFiles.map((f) => ({ name: f.name, url: f.url })) : issue.files,
        transactionId: transactionId ?? issue.transactionId,
        updatedAt: new Date().toISOString(),
      };

      // Manually moving a task to In Progress while it's assigned to an agent
      // should kick off the agent's run, same as "Send & Assign to AI" does —
      // otherwise the task sits In Progress with no work actually started. Uses
      // the same inline streaming path as handleSave so the thought process is
      // visible here too, instead of running silently in the background.
      if (nextStatus === 'inprogress' && prevStatus !== 'inprogress' && (assigneeType === 2 || assigneeType === 3) && currentAgentName) {
        const lastHumanMessage = comments.filter(c => c.role?.toLowerCase() === 'human').slice(-1)[0]?.message;
        const streamPrompt = lastHumanMessage ?? description;
        runInlineAgentStream(String(issue.id), streamPrompt, updated);
      }

      // Marking a ticket Done closes it out of the panel (like resolving a Jira
      // issue) instead of the silent in-place update other status changes get.
      if (nextStatus === 'done') onSave(updated);
      else if (onSaveSilent) onSaveSilent(updated);
      else onSave(updated);
    } catch (err: any) {
      setStatus(prevStatus);
      setApiError(err?.message || 'Failed to update status. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  }, [
    status,
    issue,
    title,
    description,
    comments,
    assigneeType,
    assigneeAgentName,
    assigneeAgentId,
    dwAgents,
    position,
    source,
    selectedCategoryId,
    uploadedFiles,
    linkedPolicyId,
    linkedPolicyNumber,
    linkedInsuredId,
    linkedInsuredName,
    linkedAgentId,
    linkedAgentName,
    onSave,
    onSaveSilent,
    runInlineAgentStream,
  ]);

  // ── "Proceed" button (board header) ─────────────────────────────────────────
  // Reuses handleStatusChange itself (same path as manually picking "In
  // Progress" from the status dropdown) rather than the autoRunPrompt/handleSave
  // path above, since that path is purpose-built for the brand-new-task create
  // flow and its dedup guard is a module-level Set keyed by prompt text, which
  // would wrongly swallow a legitimate retry of the same task/prompt later.
  const proceedSignalRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (proceedSignal == null) return;
    if (proceedSignalRef.current === proceedSignal) return;
    proceedSignalRef.current = proceedSignal;
    void handleStatusChange('inprogress');
  }, [proceedSignal, handleStatusChange]);

  const handleAssigneeChange = React.useCallback(async (rawValue: string) => {
    userChangedAssigneeRef.current = true;
    const prevAssigneeType = assigneeType;
    const prevAgentName = assigneeAgentName;
    const prevAgentId = assigneeAgentId;

    const { nextAssigneeType, nextAgentName, nextAgentId } = resolveAssigneeSelection(rawValue);
    // Switching away from a specific agent (type 2) no longer blanks agentName/
    // agentId — they're preserved as-is (see resolveAssigneeSelection), so compare
    // them directly rather than only when the new type is 2.
    const normalizedNextName = nextAgentName.trim();
    const normalizedPrevName = prevAgentName.trim();

    if (
      nextAssigneeType === prevAssigneeType &&
      normalizedNextName === normalizedPrevName &&
      nextAgentId === prevAgentId
    ) {
      return;
    }

    setAssigneeType(nextAssigneeType);
    setAssigneeAgentName(nextAgentName);
    setAssigneeAgentId(nextAgentId);
    setApiError('');

    // Draft task: keep local state only; persistence occurs on first Save/Send.
    if (!issue.id || issue.id === '__new__') return;

    const authToken = localStorage.getItem('authToken') || '';
    const transactionId = transactionIdRef.current || issue.transactionId || undefined;
    const resolvedAgentId = normalizedNextName
      ? (nextAgentId || dwAgents.find(a => a.name.trim().toLowerCase() === normalizedNextName.toLowerCase())?.id || '')
      : '';
    const apiAssigneeType = nextAssigneeType === 3 ? 2 : nextAssigneeType;
    // Agent attribution records WHICH agent works this task and is independent
    // of who currently owns it. Handing the task back to a human therefore sends
    // null for both fields, which upsert_kanban_task now treats as "leave the
    // stored value alone" -- previously this wrote the literal 'User' over the
    // real agent name and '0' over the agent id, destroying the attribution.
    const upsertAgentName = apiAssigneeType === 0 ? null : (normalizedNextName || null);
    const upsertAgentId = apiAssigneeType === 0 ? null : (resolvedAgentId || issue.agentId || null);

    setAssigneeSaving(true);
    try {
      const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/task/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          ...(transactionId ? { 'X-Transaction-Id': transactionId } : {}),
        },
        body: JSON.stringify({
          id: Number(issue.id),
          taskNumber: issue.code || null,
          statusId: KANBAN_STATUS_IDS[status],
          title,
          description,
          comments: toCommentsJson(comments),
          assigneeId: issue.assigneeId ?? 0,
          assigneeType: apiAssigneeType,
          // Only a hand-off TO an agent updates which agent type owns the task.
          // A hand-back to a human sends null so the stored value survives --
          // the agent service reads it back on a Done finalize.
          lastAssigneeType: apiAssigneeType === 0 ? null : apiAssigneeType,
          position,
          source,
          categoryId: selectedCategoryId,
          agentName: upsertAgentName,
          agentId: upsertAgentId,
          files: uploadedFiles.length
            ? JSON.stringify(uploadedFiles.map((f) => f.name))
            : null,
          transactionId,
        }),
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const b = await res.json();
          msg = b.errorMessage || b.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const updated: CommandIssue = {
        ...issue,
        title,
        description,
        status,
        assigneeType: nextAssigneeType,
        lastAssigneeType: apiAssigneeType,
        categoryId: selectedCategoryId,
        comments,
        linkedPolicyId,
        linkedPolicyNumber,
        linkedInsuredId,
        linkedInsuredName,
        linkedAgentId,
        linkedAgentName,
        agentName: normalizedNextName || undefined,
        agentId: resolvedAgentId || undefined,
        files: uploadedFiles.length ? uploadedFiles.map((f) => ({ name: f.name, url: f.url })) : issue.files,
        transactionId: transactionId ?? issue.transactionId,
        updatedAt: new Date().toISOString(),
      };

      if (onSaveSilent) onSaveSilent(updated);
      else onSave(updated);
    } catch (err: any) {
      setAssigneeType(prevAssigneeType);
      setAssigneeAgentName(prevAgentName);
      setAssigneeAgentId(prevAgentId);
      setApiError(err?.message || 'Failed to update assignee. Please try again.');
    } finally {
      setAssigneeSaving(false);
    }
  }, [
    assigneeType,
    assigneeAgentName,
    assigneeAgentId,
    resolveAssigneeSelection,
    issue,
    dwAgents,
    status,
    title,
    description,
    comments,
    position,
    source,
    selectedCategoryId,
    uploadedFiles,
    linkedPolicyId,
    linkedPolicyNumber,
    linkedInsuredId,
    linkedInsuredName,
    linkedAgentId,
    linkedAgentName,
    onSave,
    onSaveSilent,
  ]);

  const showViewDetails = mode === 'edit' && !!issue.id && issue.id !== '__new__';
  const topUploadedFiles = React.useMemo(
    () => uploadedFiles.filter((f, idx, arr) => arr.findIndex((x) => x.name.toLowerCase() === f.name.toLowerCase()) === idx),
    [uploadedFiles],
  );
  const hasTopFiles = topUploadedFiles.length > 0;
  React.useEffect(() => {
    onFilesChange?.(issue.id, topUploadedFiles.map(({ name, url }) => ({ name, url })));
  }, [issue.id, topUploadedFiles, onFilesChange]);

  // Formatting/voice buttons for the comment composer — collapsed behind the
  // "Aa" trigger and shown in a popover (attach-file stays as its own always-
  // visible icon in the row), so the composer reads as a single chat-bar-style
  // row (icons, input, Discard/Send) like the AMS chat bar.
  const commentToolbarButtons = (
    <>
      <button style={{ ...toolbarBtn, fontWeight: 700, cursor: 'pointer' }} onClick={() => applyFormat('**')}>B</button>
      <button style={{ ...toolbarBtn, fontStyle: 'italic', cursor: 'pointer' }} onClick={() => applyFormat('_')}>I</button>
      <button style={{ ...toolbarBtn, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => applyFormat('<u>', '</u>')}>U</button>
      <div style={{ width: 1, height: 16, background: border, margin: '0 2px' }} />
      <button
        title="Bullet list"
        style={{ ...toolbarBtn, cursor: 'pointer' }}
        onClick={() => {
          const ta = textareaRef.current;
          if (!ta) return;
          const start = ta.selectionStart;
          const end = ta.selectionEnd;
          const selected = newComment.slice(start, end);
          const before = newComment.slice(0, start);
          const after = newComment.slice(end);
          if (selected) {
            const bulleted = selected.split('\n').map(l => `- ${l}`).join('\n');
            setNewComment(`${before}${bulleted}${after}`);
            setTimeout(() => { ta.focus(); ta.setSelectionRange(start, start + bulleted.length); }, 0);
          } else {
            const lineStart = before.lastIndexOf('\n') + 1;
            setNewComment(`${newComment.slice(0, lineStart)}- ${newComment.slice(lineStart)}`);
            setTimeout(() => { ta.focus(); const p = start + 2; ta.setSelectionRange(p, p); }, 0);
          }
        }}
      >≡</button>
      <button
        title="Insert link"
        style={{ ...toolbarBtn, cursor: 'pointer' }}
        onClick={() => {
          const ta = textareaRef.current;
          if (!ta) return;
          const start = ta.selectionStart;
          const end = ta.selectionEnd;
          const label = newComment.slice(start, end) || 'text';
          const before = newComment.slice(0, start);
          const after = newComment.slice(end);
          const insert = `[${label}](url)`;
          setNewComment(`${before}${insert}${after}`);
          setTimeout(() => {
            ta.focus();
            const urlStart = start + label.length + 3;
            ta.setSelectionRange(urlStart, urlStart + 3);
          }, 0);
        }}
      >⊕</button>
      <button
        onClick={toggleMic}
        title={isRecording ? 'Stop recording' : 'Voice input'}
        style={{
          ...toolbarBtn,
          border: isRecording ? '1px solid rgba(239,68,68,0.5)' : `1px solid ${border}`,
          background: isRecording ? 'rgba(239,68,68,0.12)' : 'none',
          color: isRecording ? '#ef4444' : 'var(--text-secondary)',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {isRecording && (
          <span style={{
            position: 'absolute', top: 3, right: 3,
            width: 5, height: 5, borderRadius: '50%',
            background: '#ef4444',
            animation: 'mc-mic-pulse 1s ease-in-out infinite',
          }} />
        )}
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="8" y1="22" x2="16" y2="22" />
        </svg>
      </button>
    </>
  );

  // ── Avatars: the same agent faces the Digital Team strip and chat bar use ──
  const assignedAgent = dwAgents.find(a => a.id === (assigneeAgentId || issue.agentId))
    ?? dwAgents.find(a => a.name === (assigneeAgentName || '').trim());
  const agentAvatar = (size = 28) => {
    const name = displayNameForAgent(assignedAgent?.name || assigneeAgentName || 'AI', assignedAgent?.isSystem);
    const photo = agentAvatarFor(assignedAgent?.id || assigneeAgentId || issue.agentId, name);
    const box = { width: size, height: size };
    return photo
      ? <img src={photo} alt="" className="mc-avatar mc-avatar--photo" style={box} />
      : <span className="mc-avatar" style={{ ...box, background: colorForAgent(assignedAgent?.id || name) }}>{initialsForAgent(name)}</span>;
  };
  const isAgentAssignee = assigneeType !== 0 && !!(assignedAgent || (assigneeAgentName || '').trim());

  return (
    <div
      className={`mc-issue-detail${fileDragOver ? ' mc-issue-detail--dragging' : ''}`}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', overflowX: 'hidden', fontFamily: FONT, position: 'relative' }}
      onClick={() => { setStatusOpen(false); setSendDropdownOpen(false); setFormatMenuOpen(false); setAttachMenuOpen(false); }}
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {previewFile && (
        <FilePreviewModal
          name={previewFile.name}
          url={previewFile.url}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* API error toast */}
      {apiError && (
        <div role="alert" style={{ margin: '10px 16px 0', padding: '9px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171', fontSize: 13, fontFamily: FONT, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ flexShrink: 0 }}>⚠</span>
          <span style={{ flex: 1 }}>{apiError}</span>
          <button type="button" className="mc-tp-dismiss" onClick={() => setApiError('')} aria-label="Dismiss error" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0, display: 'inline-flex' }}><FiX size={15} aria-hidden="true" /></button>
        </div>
      )}

      {/* Title row: full layout for list view — Type/Assignee/Status live inline here too */}
      {onBack ? (
        <div className="mc-issue-title-row" style={{ padding: '10px 20px 14px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 8 }}>
            <div style={splitTitleRow ? { flex: '1 1 100%', minWidth: 220 } : { flex: '1 1 200px', minWidth: 160 }}>
              <input
                value={title}
                onChange={e => { setTitle(e.target.value); if (e.target.value.trim()) setTitleError(''); }}
                onFocus={() => { editingFieldRef.current.title = true; }}
                onBlur={() => { editingFieldRef.current.title = false; }}
                placeholder="Task title"
                className="mc-title-heading-input"
                style={{ width: '100%', height: 30, boxSizing: 'border-box', border: 'none', borderBottom: titleError ? '2px solid #ef4444' : `1.5px solid ${border}`, padding: '0 8px', fontSize: 15, fontWeight: 700, fontFamily: FONT, color: 'var(--text-primary)', background: 'transparent', outline: 'none', letterSpacing: '-0.01em', transition: 'background 0.15s, border-color 0.15s' }}
              />
              {titleError && <span style={{ display: 'block', fontSize: 11, color: '#ef4444', paddingLeft: 8, marginTop: 2 }}>{titleError}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', ...(splitTitleRow ? { width: '100%' } : null) }}>
              {/* Properties — quiet labeled selects, no coloured boxes. Colour is
                  only the status dot; the assignee shows who it is. */}
              <PropDropdown
                label="Type"
                value={String(selectedCategoryId)}
                items={categoryOptions.map(o => ({ value: String(o.value), label: o.label }))}
                onChange={v => setSelectedCategoryId(Number(v))}
              />
              <span style={{ display: 'contents' }} onPointerDownCapture={wantSharedAgents} onFocusCapture={wantSharedAgents}>
              <PropDropdown
                label="Assignee"
                value={assigneeSelectValue}
                leading={isAgentAssignee ? agentAvatar(20) : <span className="mc-avatar mc-avatar--me" style={{ width: 20, height: 20 }}><FiUser size={11} /></span>}
                disabled={assigneeSaving}
                onChange={v => { void handleAssigneeChange(v); }}
                items={[
                  ...ASSIGNEE_TYPES.map(a => ({ value: String(a.value), label: a.label })),
                  ...(dwAgents.length > 0 ? [
                    { kind: 'group' as const, label: 'Digital Workforce' },
                    ...dwAgents.map(a => ({ value: `dw:${a.id}`, label: a.name, starred: a.isPrimary, hint: a.isPrimary ? 'Primary agent' : undefined })),
                  ] : []),
                  ...(hasUnmatchedAgentName ? [{ value: `dw_name:${normalizedAssigneeAgentName}`, label: normalizedAssigneeAgentName }] : []),
                  ...(sharedAgents.length > 0 ? [
                    { kind: 'group' as const, label: 'Shared with' },
                    ...sharedAgents.map(a => ({ value: `agent:${a.id}`, label: a.name })),
                  ] : []),
                ]}
              />
              </span>
              <PropDropdown
                label="Status"
                value={status}
                leading={<span className="mc-prop__dot" style={{ background: currentCol.color }} aria-hidden="true" />}
                disabled={statusSaving}
                onChange={v => { void handleStatusChange(v as CommandStatus); }}
                items={COMMAND_COLUMNS.map(col => ({ value: col.key, label: col.label, color: col.color }))}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Sidebar: compact title input (sidebar header already shows code) */
        <div style={{ padding: '10px 16px 12px', flexShrink: 0, borderBottom: `1px solid ${border}` }}>
          <input
            value={title}
            onChange={e => { setTitle(e.target.value); if (e.target.value.trim()) setTitleError(''); }}
            onFocus={() => { editingFieldRef.current.title = true; }}
            onBlur={() => { editingFieldRef.current.title = false; }}
            placeholder="Task title"
            style={{ width: '100%', border: titleError ? '1.5px solid #ef4444' : `1px solid ${border}`, borderRadius: 7, padding: '7px 12px', fontSize: 15, fontWeight: 600, fontFamily: FONT, color: 'var(--text-primary)', background: inputBg, outline: 'none', boxSizing: 'border-box' }}
          />
          {titleError && <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#ef4444', paddingLeft: 2 }}>{titleError}</span>}
        </div>
      )}

      {/* Metadata row: TYPE | ASSIGNEE | STATUS — sidebar/board view only; list view shows these inline in the title row above */}
      {!onBack && (
      <div style={{ margin: '0 20px 14px', borderRadius: 10, display: 'flex', flexShrink: 0, overflow: 'hidden', background: 'var(--surface-card)', border: '1px solid var(--border-glass)' }}>
        <div style={{ flex: 1, minWidth: 0, padding: '7px 14px', borderRight: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>Type</span>
          <select className="mc-meta-select" value={selectedCategoryId} onChange={e => setSelectedCategoryId(Number(e.target.value))} style={{ background: selectBg, border: `1px solid ${border}`, borderRadius: 6, outline: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: FONT, color: 'var(--text-primary)', padding: '3px 6px', flex: 1, minWidth: 0, colorScheme: theme === 'dark' ? 'dark' : 'light' }}>
            {categoryOptions.map(o => <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: '7px 14px', borderRight: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>Assignee</span>
          <select
            className="mc-meta-select"
            value={assigneeSelectValue}
            onFocus={wantSharedAgents}
            onMouseDown={wantSharedAgents}
            onChange={e => { void handleAssigneeChange(e.target.value); }}
            disabled={assigneeSaving}
            style={{ background: selectBg, border: `1px solid ${border}`, borderRadius: 6, outline: 'none', cursor: assigneeSaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, fontFamily: FONT, color: 'var(--text-primary)', padding: '3px 6px', flex: 1, minWidth: 0, colorScheme: theme === 'dark' ? 'dark' : 'light', opacity: assigneeSaving ? 0.7 : 1 }}
          >
            {ASSIGNEE_TYPES.map(a => <option key={a.value} value={String(a.value)} style={optionStyle}>{a.label}</option>)}
            {dwAgents.length > 0 && <>
              <option disabled style={{ ...optionStyle, fontSize: 11, color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>── Digital Workforce ──</option>
              {dwAgents.map(a => <option key={`dw:${a.id}`} value={`dw:${a.id}`} style={optionStyle}>{a.isPrimary ? `★ ${a.name} (Primary)` : a.name}</option>)}
            </>}
            {hasUnmatchedAgentName && (
              <option value={`dw_name:${normalizedAssigneeAgentName}`} style={optionStyle}>
                {normalizedAssigneeAgentName}
              </option>
            )}
            {sharedAgents.length > 0 && <>
              <option disabled style={{ ...optionStyle, fontSize: 11, color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>── Shared with ──</option>
              {sharedAgents.map(a => <option key={`agent:${a.id}`} value={`agent:${a.id}`} style={optionStyle}>{a.name}</option>)}
            </>}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>Status</span>
          <select
            className="mc-meta-select"
            value={status}
            onChange={e => { void handleStatusChange(e.target.value as CommandStatus); }}
            disabled={statusSaving}
            style={{ background: selectBg, border: `1px solid ${border}`, borderRadius: 6, outline: 'none', cursor: statusSaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, fontFamily: FONT, color: currentCol.color, padding: '3px 6px', flex: 1, minWidth: 0, colorScheme: theme === 'dark' ? 'dark' : 'light', opacity: statusSaving ? 0.7 : 1 }}
          >
            {COMMAND_COLUMNS.map(col => <option key={col.key} value={col.key} style={{ ...optionStyle, color: col.color }}>{col.label}</option>)}
          </select>
        </div>
      </div>
      )}

      {/* Linked entities row — visible in both list-center and board-sidebar views. */}
      {(() => {
        // In details-header mode (onBack), linked chips are rendered near the issue id
        // in MomentumCommandPage header, so hide this below-title row.
        if (onBack) return null;
        const hasLinks = !!(linkedPolicyId || linkedInsuredId || linkedAgentId || commentLinks.some(l => l.type === 'PDF' ? l.id !== linkedPolicyId : l.id !== linkedInsuredId));
        if (!hasLinks && !hasTopFiles) return null;
        type ChipKind = 'policy' | 'insured' | 'prospect' | 'agent';
        const personKind = (id: string): ChipKind => (insuredKinds[id] === 'prospect' ? 'prospect' : 'insured');
        const allChips: { kind: ChipKind; id: string; name: string }[] = hasLinks ? [
          ...(linkedPolicyId ? [{ kind: 'policy' as ChipKind, id: linkedPolicyId, name: linkedPolicyNumber }] : []),
          ...(linkedInsuredId ? [{ kind: personKind(linkedInsuredId), id: linkedInsuredId, name: linkedInsuredName }] : []),
          ...(linkedAgentId ? [{ kind: 'agent' as ChipKind, id: linkedAgentId, name: linkedAgentName }] : []),
          ...commentLinks
            .filter(l => l.type === 'PDF' ? l.id !== linkedPolicyId : l.id !== linkedInsuredId)
            .map(l => ({ kind: l.type === 'PDF' ? 'policy' as ChipKind : personKind(l.id), id: l.id, name: l.name })),
        ] : [];
        const LIMIT = linkedLimit;
        const hasMore = allChips.length > LIMIT;
        const visible = showAllLinks ? allChips : allChips.slice(0, LIMIT);
        return (
          <div style={{ margin: '0 20px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            {hasLinks && (
            <span className="mc-linkrow__label">Linked</span>
            )}
            {visible.map((chip, i) => {
              if (chip.kind === 'policy') return (
                <span key={`policy-${chip.id}-${i}`} className="mc-linkrow__item mc-linkrow__item--policy">
                  <span className="mc-linkrow__kind">Policy:</span>
                  <button type="button" className="mc-linkrow__link" onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(chip.id); }}>{chip.name}</button>
                </span>
              );
              if (chip.kind === 'insured' || chip.kind === 'prospect') return (
                <span key={`${chip.kind}-${chip.id}-${i}`} className={`mc-linkrow__item mc-linkrow__item--${chip.kind}`}>
                  <span className="mc-linkrow__kind">{chip.kind === 'prospect' ? 'Prospect:' : 'Insured:'}</span>
                  <button type="button" className="mc-linkrow__link" onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(chip.id); }}>{chip.name}</button>
                </span>
              );
              return (
                <span key={`agent-${chip.id}-${i}`} className="mc-linkrow__item mc-linkrow__item--agent">
                  <span className="mc-linkrow__kind">Agent:</span>
                  <span className="mc-linkrow__text">{chip.name}</span>
                </span>
              );
            })}
            {hasMore && (
              <button
                onClick={() => setShowAllLinks(v => !v)}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: FONT, flexShrink: 0 }}
              >
                {showAllLinks ? 'Show less' : `+${allChips.length - LIMIT} more`}
              </button>
            )}
            {hasTopFiles && (
              <span className="mc-linkrow__label" style={{ marginLeft: hasLinks ? 6 : 0 }}>Files</span>
            )}
            {topUploadedFiles.map((file, i) => {
              const isResolving = resolvingFileName === file.name;
              return (
                <button
                  key={`top-file-${file.name}-${i}`}
                  type="button"
                  onClick={() => void handleOpenFile(file.name, file.url)}
                  title={file.name}
                  disabled={isResolving}
                  className="mc-linkrow__file"
                  aria-busy={isResolving || undefined}
                >
                  {isResolving ? <FiRefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <FiPaperclip size={11} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* File Upload — compact single row: attach button + uploaded-file pills */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUploadFile(file);
        }}
      />
      {/* Activity feed — scrollable */}
      <div ref={feedRef} className="mc-issue-feed" style={{ flex: 1, overflowY: 'auto', minHeight: 160, padding: '0 20px 8px' }}>

        {/* System / created entry */}
        {mode === 'edit' && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 18, alignItems: 'flex-start' }}>
            <span className="mc-avatar mc-avatar--system" style={{ width: 28, height: 28 }} aria-hidden="true">SY</span>
            <div style={{ flex: 1 }}>
              {(() => {
                const ticketCreatedTs = issue.createdAt || issue.updatedAt;
                return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: FONT }}>System</span>
                <span className="mc-note__action">created this task</span>
                {ticketCreatedTs && (
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: FONT }}>
                    {fmtCommentDate(ticketCreatedTs)}
                  </span>
                )}
              </div>
                );
              })()}
              {(description.trim() || showDescriptionEditor) ? (
                <textarea
                  ref={descTextareaRef}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  onFocus={() => { editingFieldRef.current.description = true; }}
                  onBlur={() => { editingFieldRef.current.description = false; }}
                  placeholder="Add a description…"
                  autoFocus={showDescriptionEditor && !description.trim()}
                  rows={1}
                  style={{ width: '100%', maxHeight: 150, border: `1px solid ${border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: FONT, color: 'var(--text-primary)', background: inputBg, outline: 'none', boxSizing: 'border-box', resize: 'none', overflowY: 'auto', lineHeight: 1.55 }}
                />
              ) : (
                /* No details from the system yet — keep the entry to one compact line instead of an empty box */
                <button
                  type="button"
                  onClick={() => setShowDescriptionEditor(true)}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: FONT, cursor: 'pointer', opacity: 0.85 }}
                >
                  + Add description
                </button>
              )}
            </div>
          </div>
        )}

        {/* Create mode description */}
        {mode === 'create' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontFamily: FONT }}>Description</div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Add a description…"
              style={{ width: '100%', border: `1px solid ${border}`, borderRadius: 7, padding: '9px 12px', fontSize: 13, fontFamily: FONT, color: 'var(--text-primary)', background: inputBg, outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: 150, lineHeight: 1.55 }}
            />
          </div>
        )}

        {/* While full task detail is hydrating in the background, always show a
            visible loading placeholder so the panel never looks "stuck" or fully
            complete before the API merge has finished. */}
        {loadingFullDetail && (
          <>
            {Array.from({ length: (issue.commentCount ?? 0) > comments.length ? 2 : 1 }).map((_, i) => (
              <div key={`skel-${i}`} className="mc-comment-skel">
                <div className="mc-skel-bone mc-comment-skel__avatar" />
                <div className="mc-comment-skel__body">
                  <div className="mc-skel-bone mc-comment-skel__line" style={{ width: '30%' }} />
                  <div className="mc-skel-bone mc-comment-skel__bubble" />
                </div>
              </div>
            ))}
          </>
        )}

        {/* Comments — rows with no message are skipped so sparse tickets don't show empty bubbles */}
        {comments.map((c, i) => {
          if (!(c.message ?? '').trim()) return null;
          const authorLabel = getCommentAuthorLabel(c, assigneeAgentName);
          const initials = getCommentInitials(c, authorLabel);
          const isHumanNote = (c.role || '').toLowerCase() === 'human';
          const noteTs = c.created || issue.updatedAt || issue.createdAt;
          // AI replies may carry explicit buttons as $$SHOWMODAL_<text><Buttons>[A|B]$$ —
          // the marker is stripped from the bubble and rendered as a prompt below it.
          const { cleaned: displayMessage, prompt: quickPrompt } = c.role !== 'Human'
            ? parseQuickPrompt(c.message)
            : { cleaned: c.message, prompt: null };
          const isOpenPrompt = c.role !== 'Human' && i === lastVisibleCommentIndex;
          // The backend decides on the run's terminal answer event (a prompt, or
          // null for "no buttons"); it applies to the newest AI note only. The
          // marker / "proceed?" heuristics cover notes with no decision — older
          // notes and runs that predate the flag.
          const backendDecided = isOpenPrompt && aiProceedPrompt !== undefined;
          const proceedPrompt = backendDecided ? (aiProceedPrompt ?? null) : quickPrompt;
          return (
            <div key={i} className={`mc-note ${isHumanNote ? 'mc-note--human' : 'mc-note--ai'}`}>
              {isHumanNote
                ? <span className="mc-avatar mc-avatar--human" style={{ width: 28, height: 28 }} aria-hidden="true">{initials}</span>
                : agentAvatar(28)}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: FONT }}>
                    {authorLabel}
                  </span>
                  {noteTs && (
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: FONT }}>
                      {fmtCommentDate(noteTs)}
                    </span>
                  )}
                </div>
                {!!displayMessage.trim() && <div className="mc-note__body mc-comment-md">
                  {renderCommentContentCached(displayMessage)}
                </div>}
                {/* Email draft cards — collapsible, editable To/Subject/Body with Send button */}
                {c.role !== 'Human' && (() => {
                  const drafts = parseEmailDrafts(c.message);
                  if (!drafts.length) return null;
                  const multiDraft = drafts.length > 1;
                  return drafts.map((original, di) => {
                    const draftKey = `${i}-${di}`;
                    const draft = getDraftValues(draftKey, original);
                    const isSent = !!sentDrafts[draftKey];
                    const isSending = sendingDraft === draftKey;
                    // First draft expanded by default; others collapsed
                    const isExpanded = expandedDrafts[draftKey] ?? (di === 0);
                    // Border/background/focus ring live in .mc-tp-draft-field so they
                    // follow the theme tokens and :focus-visible can apply.
                    const fieldStyle: React.CSSProperties = {
                      flex: 1, fontSize: 13, fontFamily: FONT, padding: '4px 8px', borderRadius: 4,
                    };
                    const fieldClass = `mc-tp-draft-field${isSent ? ' mc-tp-draft-field--sent' : ''}`;
                    const draftError = draftErrors[draftKey] || '';
                    const recipientError = !!draftError && draftError.startsWith('Add a valid recipient');
                    const ccError = !!draftError && draftError.startsWith('Check the CC');
                    const subjectError = !!draftError && draftError.startsWith('Add a subject');
                    const fieldIdBase = `mc-draft-${issue.id || 'new'}-${draftKey}`;
                    const errorId = `${fieldIdBase}-error`;
                    const labelStyle: React.CSSProperties = {
                      fontSize: 11, fontWeight: 600, color: '#a78bfa', width: 52, flexShrink: 0,
                    };
                    return (
                      <div key={`draft-${draftKey}`} style={{
                        marginTop: 8,
                        border: '1px solid rgba(139,92,246,0.25)',
                        borderRadius: 8,
                        background: 'rgba(139,92,246,0.04)',
                        padding: isExpanded ? '10px 13px' : '8px 13px',
                        fontFamily: FONT,
                      }}>
                        {/* Header — clickable to collapse/expand when multiple drafts */}
                        <div
                          className={multiDraft ? 'mc-tp-draft-toggle' : undefined}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: multiDraft ? 'pointer' : 'default', userSelect: 'none' }}
                          onClick={multiDraft ? () => setExpandedDrafts(prev => ({ ...prev, [draftKey]: !isExpanded })) : undefined}
                          {...(multiDraft ? {
                            role: 'button',
                            tabIndex: 0,
                            'aria-expanded': isExpanded,
                            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setExpandedDrafts(prev => ({ ...prev, [draftKey]: !isExpanded }));
                              }
                            },
                          } : {})}
                        >
                          {multiDraft && (
                            <FiChevronDown
                              size={13}
                              style={{
                                color: '#a78bfa', flexShrink: 0, transition: 'transform 0.15s',
                                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            />
                          )}
                          <FiMail size={13} style={{ color: '#a78bfa', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#a78bfa' }}>
                            Email Draft{multiDraft ? ` (${di + 1}/${drafts.length})` : ''}
                          </span>
                          {!isExpanded && (
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              — {draft.to}
                            </span>
                          )}
                          {isSent && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto', flexShrink: 0 }}>
                              <FiCheck size={12} /> Sent
                            </span>
                          )}
                        </div>
                        {/* Collapsible body */}
                        {isExpanded && (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <label htmlFor={`${fieldIdBase}-to`} style={labelStyle}>To:</label>
                                <input
                                  id={`${fieldIdBase}-to`}
                                  type="text"
                                  className={fieldClass}
                                  value={draft.to}
                                  readOnly={isSent}
                                  aria-invalid={recipientError || undefined}
                                  aria-describedby={recipientError ? errorId : undefined}
                                  onChange={e => updateDraftField(draftKey, original, 'to', e.target.value)}
                                  style={fieldStyle}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <label htmlFor={`${fieldIdBase}-cc`} style={labelStyle}>CC:</label>
                                <input
                                  id={`${fieldIdBase}-cc`}
                                  type="text"
                                  className={fieldClass}
                                  value={draft.cc}
                                  readOnly={isSent}
                                  placeholder="Optional"
                                  aria-invalid={ccError || undefined}
                                  aria-describedby={ccError ? errorId : undefined}
                                  onChange={e => updateDraftField(draftKey, original, 'cc', e.target.value)}
                                  style={fieldStyle}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <label htmlFor={`${fieldIdBase}-subject`} style={labelStyle}>Subject:</label>
                                <input
                                  id={`${fieldIdBase}-subject`}
                                  type="text"
                                  className={fieldClass}
                                  value={draft.subject}
                                  readOnly={isSent}
                                  aria-invalid={subjectError || undefined}
                                  aria-describedby={subjectError ? errorId : undefined}
                                  onChange={e => updateDraftField(draftKey, original, 'subject', e.target.value)}
                                  style={fieldStyle}
                                />
                              </div>
                              {draft.body && (
                                <div style={{ marginTop: 2 }}>
                                  <span id={`${fieldIdBase}-body-label`} style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Body:</span>
                                  {isSent ? (
                                    <div
                                      style={{ padding: '8px 10px', background: 'rgba(0,0,0,0.02)', borderRadius: 5, fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', maxHeight: 180, overflow: 'auto' }}
                                      dangerouslySetInnerHTML={{ __html: draft.body }}
                                    />
                                  ) : (
                                    <div
                                      contentEditable
                                      suppressContentEditableWarning
                                      role="textbox"
                                      aria-multiline="true"
                                      aria-labelledby={`${fieldIdBase}-body-label`}
                                      tabIndex={0}
                                      className="mc-tp-draft-field mc-tp-draft-body"
                                      onBlur={e => updateDraftField(draftKey, original, 'body', e.currentTarget.innerHTML)}
                                      style={{
                                        padding: '8px 10px', borderRadius: 5, fontSize: 13, lineHeight: 1.6,
                                        maxHeight: 220, overflow: 'auto', minHeight: 60,
                                      }}
                                      dangerouslySetInnerHTML={{ __html: draft.body }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                            {draftError && (
                              <div id={errorId} role="alert" className="mc-tp-draft-error">
                                {draftError}
                              </div>
                            )}
                            {!isSent && (
                              <button
                                type="button"
                                className="mc-tp-draft-send"
                                onClick={() => requestSendDraft(i, di, original)}
                                disabled={isSending}
                                aria-busy={isSending || undefined}
                                style={{
                                  marginTop: 10,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  padding: '6px 16px',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  fontFamily: FONT,
                                  color: '#fff',
                                  background: isSending ? '#7c3aed88' : '#7c3aed',
                                  border: 'none',
                                  borderRadius: 6,
                                  cursor: isSending ? 'not-allowed' : 'pointer',
                                }}
                              >
                                <FiSend size={11} />
                                {isSending ? 'Sending…' : 'Send email'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  });
                })()}
                {c.role !== 'Human' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                    <button
                      onClick={() => handleViewDetailsForComment(c, i)}
                      title="View details for this chat response"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid rgba(124,58,237,0.28)',
                        background: 'rgba(124,58,237,0.08)',
                        color: theme === 'dark' ? '#c4b5fd' : '#6d28d9',
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: FONT,
                        cursor: 'pointer',
                      }}
                    >
                      <FiBarChart2 size={11} /> View Details
                    </button>
                    {c.id && (
                      <>
                        <button
                          onClick={() => handleToggleCommentLike(i)}
                          title={c.isLiked ? 'Helpful - Click to remove' : 'Mark as helpful'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: c.isLiked ? '#10b981' : 'var(--text-secondary)' }}
                        >
                          <FiThumbsUp size={12} />
                        </button>
                        <button
                          onClick={() => handleToggleCommentDislike(i)}
                          title={c.isDisliked ? 'Not helpful - Click to remove' : 'Mark as not helpful'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: c.isDisliked ? '#ef4444' : 'var(--text-secondary)' }}
                        >
                          <FiThumbsDown size={12} />
                        </button>
                        <button
                          onClick={(e) => handleOpenCommentFeedbackEditor(i, e)}
                          title={c.feedback ? 'Edit feedback' : 'Add feedback'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: c.feedback ? '#a78bfa' : 'var(--text-secondary)' }}
                        >
                          <FiEdit3 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <FeedbackEditorPopover
          open={feedbackEditorIndex !== null && !!feedbackEditorPos}
          position={feedbackEditorPos}
          value={feedbackDraft}
          onChange={setFeedbackDraft}
          onClose={closeCommentFeedbackEditor}
          onSave={handleSaveCommentFeedback}
          title="Edit Feedback"
          placeholder="Add your feedback here..."
          fontFamily={FONT}
          borderColor={border}
          backgroundColor={popoverBg}
          titleColor="var(--text-primary)"
          cancelColor="var(--text-secondary)"
          saveColor="#a78bfa"
          saveBackgroundColor="rgba(139,92,246,0.16)"
        />

        {/* Busy indicator and skeleton shown while create/upsert/AI-stream is in-flight. */}
        {showBusyIndicator && (
          <div className="thinking-indicator" style={{ marginBottom: showBusySkeleton ? 8 : 12 }}>
            <div className="thinking-indicator__shimmer" />
            <span className="thinking-indicator__text">
              <RiSparklingFill size={12} className="thinking-indicator__icon" />
              {busyLabel}
            </span>
          </div>
        )}
        {showBusySkeleton && (
          <div style={{ marginBottom: 12 }}>
            <div className="mc-comment-skel" style={{ marginBottom: 0 }}>
              <div className="mc-skel-bone mc-comment-skel__avatar" />
              <div className="mc-comment-skel__body">
                <div className="mc-skel-bone mc-comment-skel__line" style={{ width: '42%' }} />
                <div className="mc-skel-bone mc-comment-skel__bubble" />
              </div>
            </div>
          </div>
        )}

        {/* ThinkingBlock — stays visible and expanded for the whole run so the thought
            process is readable while the answer streams; cleared once the response arrives */}
        {thoughtText && (
          <div style={{ marginBottom: 12 }}>
            <ThinkingBlock
              thoughtText={thoughtText}
              currentTool={currentTool || undefined}
              isActiveThinking={isThinking}
              hasAnswer={false}
            />
          </div>
        )}

        {/* Streaming answer bubble — live while answer is being received */}
        {streamingContent && (
          <div className="mc-note mc-note--ai">
            {agentAvatar(28)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span className="mc-note__author">{getCommentAuthorLabel({ role: 'AI', message: '' }, assigneeAgentName)}</span>
                <span className="mc-note__live"><span className="mc-note__live-dot" aria-hidden="true" />Replying…</span>
              </div>
              <div className="mc-note__body mc-comment-md">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={linkComponents}>{processEntityLinks(streamingContent)}</ReactMarkdown>
                <span style={{ display: 'inline-block', width: 2, height: 13, background: '#a200ff', marginLeft: 1, verticalAlign: 'text-bottom', animation: 'mc-cursor-blink 1s step-end infinite' }} />
              </div>
            </div>
          </div>
        )}

        {/* The run ended without an answer — most often BUDGET_REACHED (the
            task's AI session hit its spending budget and was paused). Persistent
            and dismissible, unlike the 4.5s apiError toast; the backend also
            saves the same text as an AI note, which the task poll brings in. */}
        {aiRunError && !streamingContent && !isThinking && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-start' }} role="alert">
            <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#f87171', fontFamily: FONT }}>
              AI
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: FONT }}>AI Agent</span>
                <span style={{ fontSize: 11, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 5, padding: '2px 8px', color: '#f87171', fontWeight: 600, fontFamily: FONT }}>
                  {aiRunError.code === BUDGET_REACHED_CODE ? 'Budget reached' : 'Run stopped'}
                </span>
              </div>
              <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '0 8px 8px 8px', padding: '10px 13px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, wordBreak: 'break-word', fontFamily: FONT, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ flexShrink: 0 }}>⚠</span>
                <span style={{ flex: 1 }}>{aiRunError.message}</span>
                <button type="button" className="mc-tp-dismiss" onClick={() => setAiRunError(null)} aria-label="Dismiss error" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0, display: 'inline-flex' }}><FiX size={15} aria-hidden="true" /></button>
              </div>
            </div>
          </div>
        )}

        {/* Part of the answer has arrived but the next chunk is stalled — same
            animated-logo "AI is responding" indicator as the chat page, instead
            of the plain skeleton, so a mid-stream pause doesn't look stuck. */}
        {streamingContent && (sendBusy || isThinking) && !thoughtText && showStreamGapIndicator && (
          <div className="chat-msg__stream-gap" style={{ marginBottom: 12, marginLeft: 44 }} aria-live="polite" aria-label="Response is still streaming">
            <div className="chat-msg__stream-gap-head">
              <div className="chat-msg__stream-gap-logo-wrap" aria-hidden="true">
                <span className="chat-msg__stream-gap-ring chat-msg__stream-gap-ring--outer" />
                <span className="chat-msg__stream-gap-ring chat-msg__stream-gap-ring--inner" />
                <img src={aiLogo} alt="" className="chat-msg__stream-gap-logo" />
              </div>
              <div className="chat-msg__stream-gap-meta">
                <span className="chat-msg__stream-gap-title">Insurance Agency AI is responding</span>
                <span className="chat-msg__stream-gap-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Skeleton loader shown after streaming content to indicate more is being received */}
        {streamingContent && (sendBusy || isThinking) && !thoughtText && !showStreamGapIndicator && (
          <div style={{ marginBottom: 12, marginLeft: 44 }}>
            <div className="mc-comment-skel" style={{ marginBottom: 0 }}>
              <div className="mc-comment-skel__body">
                <div className="mc-skel-bone mc-comment-skel__line" style={{ width: '65%' }} />
                <div className="mc-skel-bone mc-comment-skel__bubble" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comment editor — single chat-bar-style row: formatting icon, input,
          Discard/Send, mirroring the AMS chat bar layout. */}
      {!barIsComposer && (
      <div className={`mc-comment-editor-wrap${barIsComposer ? ' mc-comment-editor-wrap--bar' : ''}`} style={{ borderTop: `1px solid ${border}`, flexShrink: 0, padding: onBack ? '8px 16px 14px' : '8px 14px 14px' }}>
        {fileDragOver && (
          <div className="mc-issue-drop-overlay" aria-hidden="true">
            <FiPaperclip size={24} />
            <span>Drop files to attach</span>
          </div>
        )}
        {(commentAttachedFiles.length > 0 || pendingUploads.length > 0 || uploadError) && (
          <div style={{ margin: '0 0 10px', display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              {(commentAttachedFiles.length > 0 || pendingUploads.length > 0) && (
                <div
                  ref={uploadedFilesWrapRef}
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    gap: 6,
                    paddingTop: 1,
                    ...(showAllUploadedFiles
                      ? { flexWrap: 'wrap' as const }
                      : { overflow: 'hidden' as const, height: 44 }),
                  }}
                >
                  {commentAttachedFiles.map((f) => {
                    const isResolving = resolvingFileName === f.name;
                    const chipStyle: React.CSSProperties = {
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 38,
                      padding: '0 10px',
                      borderRadius: 10,
                      background: 'rgba(139,92,246,0.08)',
                      border: `1px solid ${border}`,
                      color: 'var(--text-secondary)',
                      fontFamily: FONT,
                      cursor: isResolving ? 'wait' : 'pointer',
                      flexShrink: 0,
                      maxWidth: 260,
                      opacity: isResolving ? 0.75 : 1,
                    };
                    const inner = (
                      <>
                        <span style={{ width: 20, height: 20, borderRadius: 6, border: `1px solid ${border}`, background: 'rgba(139,92,246,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {isResolving ? <FiRefreshCw size={11} style={{ opacity: 0.9, animation: 'spin 1s linear infinite' }} /> : <FiPaperclip size={11} style={{ opacity: 0.9 }} />}
                        </span>
                        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: 'var(--text-primary)' }}>
                            {f.name}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                            {formatFileSize(f.size)}
                          </span>
                        </span>
                      </>
                    );
                    return (
                      <button
                        type="button"
                        key={f.transactionId}
                        className="mc-tp-chip-btn"
                        style={chipStyle}
                        title={`Preview ${f.name}`}
                        aria-label={`Preview ${f.name}`}
                        aria-busy={isResolving || undefined}
                        onClick={(e) => { e.stopPropagation(); if (!isResolving) void handleOpenFile(f.name, f.url); }}
                      >
                        {inner}
                      </button>
                    );
                  })}
                  {pendingUploads.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        height: 38,
                        padding: '0 10px',
                        borderRadius: 10,
                        background: 'rgba(139,92,246,0.08)',
                        border: `1px dashed ${border}`,
                        color: 'var(--text-secondary)',
                        fontFamily: FONT,
                        flexShrink: 0,
                        maxWidth: 260,
                      }}
                      title={`${p.name} (uploading)`}
                    >
                      <span style={{ width: 20, height: 20, borderRadius: 6, border: `1px solid ${border}`, background: 'rgba(139,92,246,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FiRefreshCw size={11} style={{ opacity: 0.9, animation: 'spin 1s linear infinite' }} />
                      </span>
                      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: 'var(--text-primary)' }}>
                          {p.name}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                          Uploading...
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(hasUploadedOverflow || showAllUploadedFiles) && (
                <button
                  type="button"
                  onClick={() => setShowAllUploadedFiles((v) => !v)}
                  style={{
                    background: 'rgba(139,92,246,0.12)',
                    border: `1px solid ${border}`,
                    borderRadius: 'var(--radius-xs)',
                    height: 22,
                    padding: '0 9px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    fontFamily: FONT,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {showAllUploadedFiles ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
            {uploadError && (
              <div style={{ fontSize: 11, color: '#f87171', fontFamily: FONT }}>{uploadError}</div>
            )}
          </div>
        )}
        {!barIsComposer && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          {/* Formatting trigger — reveals B/I/U/bullet/link/mic in a popover above */}
          <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setFormatMenuOpen(o => !o)}
              title="Formatting"
              style={{ ...toolbarBtn, width: COMPOSER_CONTROL_PX, height: COMPOSER_CONTROL_PX, cursor: 'pointer', position: 'relative' }}
            >
              Aa
              {isRecording && (
                <span style={{ position: 'absolute', top: 3, right: 3, width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'mc-mic-pulse 1s ease-in-out infinite' }} />
              )}
            </button>
            {formatMenuOpen && (
              <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', maxWidth: 260, background: popoverBg, border: `1px solid ${border}`, borderRadius: 8, padding: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50 }}>
                {commentToolbarButtons}
              </div>
            )}
          </div>

          {/* Textarea + mention dropdown (portal to escape overflow:hidden) */}
          <div className={`mc-comment-field-wrap${fileDragOver ? ' mc-comment-field-wrap--dragging' : ''}`} ref={commentBoxRef} style={{ position: 'relative', display: 'flex', flex: 1, minWidth: 0 }}>
            {/* Ghost mirror — renders behind textarea to highlight inserted @mentions */}
            {insertedNames.length > 0 && (
              <div
                ref={mirrorRef}
                aria-hidden="true"
                style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  padding: '7px 12px', fontSize: 13, fontFamily: FONT, lineHeight: '20px',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  overflowY: 'hidden', pointerEvents: 'none', color: 'transparent', zIndex: 1,
                }}
              >
                {renderHighlightedText(newComment)}
              </div>
            )}
            {mentionActive && mentionPos && ReactDOM.createPortal(
              <div
                className="mc-mention-portal"
                style={{
                  position: 'fixed',
                  bottom: `calc(100vh - ${mentionPos.top}px)`,
                  left: mentionExpanded
                    ? Math.max(0, Math.min(mentionPos.left, window.innerWidth - 592))
                    : Math.max(0, Math.min(mentionPos.left, window.innerWidth - 348)),
                  zIndex: 9999,
                }}
              >
                <InsuredMentionDropdown
                  query={mentionQuery}
                  results={mentionResults}
                  loading={mentionLoading}
                  activeIdx={mentionActiveIdx}
                  onSelect={ins => {
                    setNewComment(selectMention(ins, newComment));
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }}
                  onActiveChange={setMentionActiveIdx}
                  onExpandedChange={setMentionExpanded}
                />
              </div>,
              document.body
            )}
            <textarea
              ref={textareaRef}
              value={newComment}
              onChange={e => { setNewComment(e.target.value); mentionValueChange(e.target.value); }}
              onPaste={(e) => {
                const files = readFilesFromDataTransfer(e.clipboardData);
                if (!files.length) return;
                e.preventDefault();
                e.stopPropagation();
                void handleUploadFiles(files);
              }}
              onScroll={() => { if (mirrorRef.current && textareaRef.current) mirrorRef.current.scrollTop = textareaRef.current.scrollTop; }}
              onKeyDown={e => {
                const consumed = mentionKeyDown(e, newComment, val => {
                  setNewComment(val);
                  setTimeout(() => textareaRef.current?.focus(), 0);
                });
                  if (!consumed && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!isComposerBusy) handleReply(); }
              }}
              placeholder="Add a comment… Type @ to link insured, policy, or quote"
              rows={1}
              style={{ width: '100%', minHeight: COMPOSER_CONTROL_PX, maxHeight: COMMENT_BOX_MAX_PX, border: `1px solid ${border}`, borderRadius: 8, padding: '7px 12px', fontSize: 13, fontFamily: FONT, color: 'var(--text-primary)', background: insertedNames.length > 0 ? 'transparent' : inputBg, outline: 'none', boxSizing: 'border-box', resize: 'none', overflowY: 'auto', lineHeight: '20px', position: 'relative', zIndex: 2 }}
            />
          </div>

          {/* Attach file — sits at the end of the input box, before Discard/Send.
              Opens a menu offering a local file upload or picking from AMS Drive. */}
          <div style={{ position: 'relative', display: 'flex', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setAttachMenuOpen(o => !o)}
              disabled={uploadingFile}
              title={uploadingFile ? 'Uploading…' : 'Attach a file to this task'}
              style={{
                ...toolbarBtn,
                width: COMPOSER_CONTROL_PX,
                height: COMPOSER_CONTROL_PX,
                flexShrink: 0,
                cursor: uploadingFile ? 'not-allowed' : 'pointer',
                opacity: uploadingFile ? 0.7 : 1,
              }}
            >
              {uploadingFile
                ? <FiRefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                : <FiPaperclip size={13} />}
            </button>
            {attachMenuOpen && (
              <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, minWidth: 190, background: popoverBg, border: `1px solid ${border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50, overflow: 'hidden' }}>
                <div style={{ padding: '8px 16px 4px', fontSize: 11, color: 'var(--text-secondary)', fontFamily: FONT }}>Choose upload type:</div>
                <button
                  type="button"
                  onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                  style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: FONT, color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                >
                  <FiPaperclip size={13} /> Upload file
                </button>
                <button
                  type="button"
                  onClick={() => { setAttachMenuOpen(false); setShowAmsSelector(true); }}
                  style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: FONT, color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                >
                  <FiFolder size={13} /> Select from AMS
                </button>
              </div>
            )}
          </div>

          {/* Split send button — Discard now lives in this dropdown alongside
              Send & Assign to AI, instead of its own icon in the row. */}
          <div ref={sendDropdownRef} style={{ position: 'relative', display: 'flex', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button type="button" onClick={handleReply} disabled={isComposerBusy} title="Send" aria-label="Send" style={{ height: COMPOSER_CONTROL_PX, width: 42, borderRadius: '7px 0 0 7px', background: isComposerBusy ? 'rgba(109,40,217,0.4)' : 'linear-gradient(135deg, #6d28d9, #a200ff)', color: '#fff', border: 'none', borderRight: '1px solid rgba(255,255,255,0.18)', cursor: isComposerBusy ? 'not-allowed' : 'pointer', opacity: isComposerBusy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isComposerBusy ? <FiRefreshCw size={13} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} /> : <FiSend size={13} aria-hidden="true" />}
            </button>
            <button
              type="button"
              ref={sendMenuTriggerRef}
              onClick={() => setSendDropdownOpen(o => !o)}
              onKeyDown={e => {
                if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isComposerBusy) { e.preventDefault(); setSendDropdownOpen(true); }
                else if (e.key === 'Escape' && sendDropdownOpen) { e.preventDefault(); setSendDropdownOpen(false); }
              }}
              disabled={isComposerBusy}
              aria-label="More send options"
              aria-haspopup="menu"
              aria-expanded={sendDropdownOpen}
              aria-controls={sendDropdownOpen ? 'mc-tp-send-menu' : undefined}
              style={{ height: COMPOSER_CONTROL_PX, width: 30, borderRadius: '0 7px 7px 0', background: isComposerBusy ? 'rgba(109,40,217,0.4)' : 'linear-gradient(135deg, #6d28d9, #a200ff)', color: '#fff', border: 'none', fontSize: 13, cursor: isComposerBusy ? 'not-allowed' : 'pointer', opacity: isComposerBusy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiChevronDown size={13} aria-hidden="true" />
            </button>
            {sendDropdownOpen && (
              <div
                id="mc-tp-send-menu"
                ref={sendMenuRef}
                role="menu"
                aria-label="Send options"
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    setSendDropdownOpen(false);
                    sendMenuTriggerRef.current?.focus();
                  } else if (e.key === 'Tab') {
                    setSendDropdownOpen(false);
                  }
                }}
                style={{ position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, minWidth: 220, background: popoverBg, border: `1px solid ${border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50, overflow: 'hidden' }}>
                <button
                  type="button"
                  role="menuitem"
                  className="mc-tp-menuitem"
                  onClick={() => {
                    setSendDropdownOpen(false);
                    if (newComment.trim() || commentAttachedFiles.length > 0) setDiscardConfirmOpen(true);
                    else onClose();
                  }}
                  disabled={isComposerBusy}
                  style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: FONT, color: 'var(--text-secondary)', cursor: isComposerBusy ? 'not-allowed' : 'pointer', opacity: isComposerBusy ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                  <FiX size={14} aria-hidden="true" /> Discard
                </button>
              </div>
            )}
          </div>
        </div>
        )}


      </div>
      )}

      {/* Polite, low-noise announcements for the AI run — start and finish
          only, never the token stream itself. */}
      <div className="mc-tp-sr-only" role="status" aria-live="polite" aria-atomic="true">{aiLiveMessage}</div>

      {confirmDraft && (() => {
        const d = draftEdits[confirmDraft.key] || confirmDraft.original;
        const preview = draftBodyPreview(d.body);
        return (
          <TaskConfirmDialog
            idBase="mc-tp-send-email"
            title="Send this email?"
            summary={(
              <dl className="mc-tp-email-summary">
                <dt>To</dt><dd>{splitRecipients(d.to).join(', ')}</dd>
                {splitRecipients(d.cc || '').length > 0 && (<><dt>CC</dt><dd>{splitRecipients(d.cc).join(', ')}</dd></>)}
                <dt>Subject</dt><dd>{d.subject}</dd>
                {preview.length > 0 && (<><dt>Body</dt><dd className="mc-tp-email-summary__body">{preview.map((l, idx) => <span key={idx}>{l}</span>)}</dd></>)}
              </dl>
            )}
            description="Review the recipients before sending. Once sent, the email can't be recalled."
            confirmLabel="Send email"
            cancelLabel="Keep editing"
            onCancel={() => setConfirmDraft(null)}
            onConfirm={() => {
              const c = confirmDraft;
              setConfirmDraft(null);
              void handleSendDraft(c.commentIndex, c.draftIndex, c.original);
            }}
          />
        );
      })()}

      {discardConfirmOpen && (
        <TaskConfirmDialog
          idBase="mc-tp-discard"
          title="Discard this reply?"
          description="Your unsent text will be lost."
          confirmLabel="Discard"
          cancelLabel="Keep writing"
          tone="danger"
          onCancel={() => { setDiscardConfirmOpen(false); setTimeout(() => textareaRef.current?.focus(), 0); }}
          onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        />
      )}

      {/* Select from AMS Drive — browse a client's AMS files and attach them to this task */}
      <SelectFromAmsDialog
        visible={showAmsSelector}
        onClose={() => setShowAmsSelector(false)}
        onUpload={handleAmsFilesUpload}
      />
    </div>
  );
};

export default CommandIssuePanel;


